import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import './App.css';
import { getQuarters, getMyPlan, saveOKRs, parseOKRs, createCapture, updateCapture, linkCapture, deleteCapture, getHomeSuggestion, getTourCompleted, updateTourCompleted, getOKRs, updateObjective, updateKR, createKR, deleteKR, deleteObjective } from './api';
import OnboardingTour from './OnboardingTour';
import SlackCommandsModal from './SlackCommandsModal';
import FeedbackWidget from './FeedbackWidget';
import ProfilePage from './ProfilePage';

// Environment configuration
// Get API base URL from environment or fallback to prod custom domain
const getApiUrl = () => {
  // REACT_APP_API_URL should be set at build time for Cloudflare Pages
  return process.env.REACT_APP_API_URL || 'https://api.getdecisionos.com';
};

const ENV_CONFIG = {
  API_URL: getApiUrl(), // Always production - simple
  FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL || window.location.origin,
  DEV_MODE: process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_MODE !== 'false',
  IS_PRODUCTION: process.env.NODE_ENV === 'production'
};

// Log environment config on load to verify it's correct
console.log('[ENV_CONFIG] ========== ENVIRONMENT CONFIG ==========');
console.log('[ENV_CONFIG] Window location:', window.location.href);
console.log('[ENV_CONFIG] NODE_ENV:', process.env.NODE_ENV);
console.log('[ENV_CONFIG] REACT_APP_API_URL (from env):', process.env.REACT_APP_API_URL);
console.log('[ENV_CONFIG] Final API_URL:', ENV_CONFIG.API_URL);
console.log('[ENV_CONFIG] FRONTEND_URL:', ENV_CONFIG.FRONTEND_URL);
console.log('[ENV_CONFIG] DEV_MODE:', ENV_CONFIG.DEV_MODE);
console.log('[ENV_CONFIG] =======================================');

// Date utility functions - timezone-safe date handling for "YYYY-MM-DD" strings
// Get today's date as "YYYY-MM-DD" in a specific timezone
// This matches the backend's date generation logic
const getTodayInTimezone = (timezone = 'America/Los_Angeles') => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(new Date()); // Returns "YYYY-MM-DD"
};

// Add days to a "YYYY-MM-DD" date string
const addDaysToDateString = (dateStr, days) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Get effective due date (DRY - single source of truth for snooze logic)
// Returns snoozeUntil if set and in future, else dueDate
const getEffectiveDueDate = (task, todayStr) => {
  // If snoozed to a future date, use snooze date
  if (task.snoozeUntil && task.snoozeUntil > todayStr) {
    return task.snoozeUntil;
  }
  // Otherwise use original due date
  return task.dueDate;
};

// Reusable Action Button with validation state
function ActionButton({ 
  onClick, 
  isValid = true, 
  validationMessage = 'Please fill in all required fields',
  children,
  className = '',
  disabled = false,
  ...props 
}) {
  const isDisabled = disabled || !isValid;
  
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      title={isDisabled && validationMessage ? validationMessage : ''}
      className={`${className} ${isDisabled ? 'btn-disabled' : ''}`}
      {...props}
    >
      {children}
    </button>
  );
}

// Helper to check if user has valid session
// HttpOnly cookies can't be read by JavaScript, so we check via API

// Session check cache - stores both authenticated and enabled status
const sessionCheckCache = { value: null, enabled: null, timestamp: 0, ttl: 5000 }; // Cache for 5 seconds

async function hasValidSession() {
  // In dev mode with DEV_MODE=true, bypass auth
  if (ENV_CONFIG.DEV_MODE) return { authenticated: true, enabled: true };
  
  // Use cached result if recent
  const now = Date.now();
  if (sessionCheckCache.value !== null && (now - sessionCheckCache.timestamp) < sessionCheckCache.ttl) {
    return { authenticated: sessionCheckCache.value, enabled: sessionCheckCache.enabled };
  }
  
  try {
    console.log('[AUTH_DEBUG] ========== FRONTEND SESSION CHECK ==========');
    console.log('[AUTH_DEBUG] API URL:', ENV_CONFIG.API_URL);
    console.log('[AUTH_DEBUG] Checking:', `${ENV_CONFIG.API_URL}/api/auth/check`);
    console.log('[AUTH_DEBUG] Document cookies:', document.cookie);
    
    const response = await fetch(`${ENV_CONFIG.API_URL}/api/auth/check`, {
      credentials: 'include', // Include cookies
      cache: 'no-store' // Don't cache this request
    });
    
    console.log('[AUTH_DEBUG] Response status:', response.status, response.statusText);
    console.log('[AUTH_DEBUG] Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[AUTH_DEBUG] Session check failed:', response.status, response.statusText, errorText);
      sessionCheckCache.value = false;
      sessionCheckCache.enabled = false;
      sessionCheckCache.timestamp = now;
      return { authenticated: false, enabled: false, distinctId: null };
    }
    
    const data = await response.json();
    console.log('[AUTH_DEBUG] Response data:', JSON.stringify(data, null, 2));
    const authenticated = data.ok && data.authenticated === true;
    const enabled = data.ok && data.enabled === true;
    const distinctId = data.distinctId || null;
    
    console.log('[AUTH_DEBUG] Session check result:', JSON.stringify({ authenticated, enabled, ok: data.ok }, null, 2));
    console.log('[AUTH_DEBUG] ============================================');
    
    // Cache the result
    sessionCheckCache.value = authenticated;
    sessionCheckCache.enabled = enabled;
    sessionCheckCache.timestamp = now;
    
    return { authenticated, enabled, distinctId };
  } catch (error) {
    console.error('[AUTH_DEBUG] Session check exception:', error);
    console.error('[AUTH_DEBUG] Error stack:', error.stack);
    // On error, assume not authenticated
    sessionCheckCache.value = false;
    sessionCheckCache.enabled = false;
    sessionCheckCache.timestamp = now;
    return { authenticated: false, enabled: false, distinctId: null };
  }
}

// Helper function to generate quarters: 1 past, current, 3 future
function generateQuarterOptions() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11
  
  // Determine current quarter (1-4)
  const currentQ = Math.floor(currentMonth / 3) + 1;
  
  // Generate array of quarter objects [{year, q}, ...]
  const quarters = [];
  
  // Start from 1 quarter ago
  let year = currentYear;
  let q = currentQ - 1;
  
  // Normalize if q is 0 (means Q4 of previous year)
  if (q === 0) {
    q = 4;
    year -= 1;
  }
  
  // Generate 5 quarters: 1 past + current + 3 future
  for (let i = 0; i < 5; i++) {
    quarters.push({ year, q });
    
    // Increment quarter
    q += 1;
    if (q > 4) {
      q = 1;
      year += 1;
    }
  }
  
  // Convert to "YYYY-Q#" format
  return quarters.map(({ year, q }) => `${year}-Q${q}`);
}

// Protected Route Wrapper - Redirects to login if no session, or to not-enabled if disabled
function ProtectedRoute({ children, onAuthSuccess }) {
  const location = useLocation();
  const posthog = usePostHog();
  const [authStatus, setAuthStatus] = useState(null); // null = checking, {authenticated, enabled} = checked
  
  useEffect(() => {
    hasValidSession().then(status => {
      setAuthStatus(status);
      // If authenticated successfully, reload quarters
      if (status.authenticated && status.enabled && onAuthSuccess) {
        onAuthSuccess();
      }
      // Identify user in PostHog for analytics (who did what)
      if (status.authenticated && status.distinctId && posthog) {
        posthog.identify(status.distinctId);
      }
    });
  }, [onAuthSuccess, posthog]);
  
  // Show nothing while checking
  if (authStatus === null) {
    return null;
  }
  
  if (!authStatus.authenticated) {
    // Redirect to login, but save the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (!authStatus.enabled) {
    // Redirect to not-enabled page
    return <Navigate to="/not-enabled" replace />;
  }
  
  return children;
}

// Mock session data - replace with real auth later
const mockSession = {
  role: 'ic', // 'ic', 'lead', 'admin'
  team: 'Engineering',
  userId: 'user123'
};

// Mock data for polling
// Generate dynamic current quarter
function getCurrentQuarter() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11
  const currentQ = Math.floor(currentMonth / 3) + 1;
  return `${currentYear}-Q${currentQ}`;
}

const mockData = {
  drift: 0, // Will be loaded from API via Home page
  approvals: 3,
  currentQuarter: getCurrentQuarter(),
  availableQuarters: generateQuarterOptions()
};

// Mock quarter lock status
const mockQuarterLock = {
  isLocked: false,
  lockDate: '2025-03-31'
};

function App() {
  const [railCollapsed, setRailCollapsed] = useState(mockSession.role === 'ic');
  const [data, setData] = useState(mockData);
  const [testSlackId, setTestSlackId] = useState(''); // Dev mode only - enter Slack ID for local testing
  const [realDrift, setRealDrift] = useState(null); // Real drift from API (null until loaded)
  const [toast, setToast] = useState(null); // Toast notification state (for minor actions)
  const [feedbackModal, setFeedbackModal] = useState(null); // Modal for major milestones/errors

  // Load quarters from API
  // This runs after successful authentication (not on mount)
  const loadQuarters = useCallback(async () => {
    try {
      console.log('[QUARTERS_LOAD] Starting quarter load...');
        const result = await getQuarters();
      console.log('[QUARTERS_LOAD] API response:', result);
        const availableQuarters = result.quarters || mockData.availableQuarters;
      
      // If API returned empty quarters, use mock data
      if (!availableQuarters || availableQuarters.length === 0) {
        console.log('[QUARTERS_LOAD] No quarters from API, using mock data');
        return; // Don't update state, keep mock data
      }
      
      // Check if user has a quarter preference saved in sessionStorage
      const savedQuarter = sessionStorage.getItem('selectedQuarter');
        
        setData(prev => {
        // Priority: savedQuarter (if valid) > current quarter (if valid) > first available
        let newCurrentQuarter;
        
        if (savedQuarter && availableQuarters.includes(savedQuarter)) {
          // Use saved quarter if it's still valid
          newCurrentQuarter = savedQuarter;
        } else if (availableQuarters.includes(prev.currentQuarter)) {
          // Use current quarter if it's valid
          newCurrentQuarter = prev.currentQuarter;
        } else {
          // Default to first available (most recent)
          newCurrentQuarter = availableQuarters[0] || prev.currentQuarter;
        }
        
        // Save to sessionStorage for persistence
        sessionStorage.setItem('selectedQuarter', newCurrentQuarter);
          
          console.log('[QUARTERS_LOAD] Available quarters:', availableQuarters);
        console.log('[QUARTERS_LOAD] Saved quarter:', savedQuarter);
          console.log('[QUARTERS_LOAD] Setting currentQuarter to:', newCurrentQuarter);
          
          return {
            ...prev,
            availableQuarters: availableQuarters,
            currentQuarter: newCurrentQuarter
          };
        });
      } catch (error) {
      console.error('[QUARTERS_LOAD] Failed to load quarters:', error);
      // Silent fail - will use mock data (don't update state)
      }
  }, []);
  
  // Don't load quarters on mount - wait for ProtectedRoute to call loadQuarters after auth
  // useEffect(() => {
  //   loadQuarters();
  // }, [loadQuarters]);

  // Note: Drift is now loaded by the Home page via /api/home/suggestion
  // which calls setRealDrift(). This eliminates duplicate API calls.
  // If you need drift on other pages, they can call getDrift() individually.

  // Polling every 45 seconds
  useEffect(() => {
    const pollData = async () => {
      try {
        // TODO: Replace with real API calls for drift and approvals
        // For now, keep static data to avoid random changes
      setData(prev => ({
        ...prev,
          // drift: await getDrift(),
          // approvals: await getProposals('Pending').length
      }));
      } catch (error) {
        console.error('Failed to poll data:', error);
      }
    };

    const interval = setInterval(pollData, 45000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Never trigger global shortcuts while the user is typing in an input
      // (prevents accidental navigation like typing "go" in Add Task)
      const target = e.target;
      const tagName = target?.tagName?.toLowerCase?.();
      const isTypingContext =
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target?.isContentEditable === true;
      if (isTypingContext) return;

      // Toggle rail with [
      if (e.key === '[') {
        setRailCollapsed(!railCollapsed);
      }
      
      // Navigation shortcuts (g o = overview, g p = plan)
      if (e.key === 'g') {
        // Set a flag to catch the next key
        window.__waitingForNavKey = true;
        setTimeout(() => {
          window.__waitingForNavKey = false;
        }, 1000);
      } else if (window.__waitingForNavKey) {
        if (e.key === 'o') {
          window.location.href = '/overview';
        } else if (e.key === 'p') {
          window.location.href = '/plan';
        }
        window.__waitingForNavKey = false;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [railCollapsed]);

  const isLeadOrAdmin = mockSession.role === 'lead' || mockSession.role === 'admin';

  return (
    <>
    <Router>
      <AppContent 
        railCollapsed={railCollapsed}
        setRailCollapsed={setRailCollapsed}
        data={data}
        setData={setData}
        testSlackId={testSlackId}
        setTestSlackId={setTestSlackId}
        realDrift={realDrift}
        setRealDrift={setRealDrift}
        isLeadOrAdmin={isLeadOrAdmin}
          toast={toast}
          setToast={setToast}
          feedbackModal={feedbackModal}
          setFeedbackModal={setFeedbackModal}
          loadQuarters={loadQuarters}
      />
    </Router>
      
      {/* Toast Notification - Minor actions only */}
      {toast && (
        <div className="toast-notification">
          {toast}
        </div>
      )}

      {/* Feedback Modal - Major milestones and errors */}
      {feedbackModal && (
        <div className="feedback-modal-overlay" onClick={() => {
          if (feedbackModal.dismissable !== false) {
            setFeedbackModal(null);
          }
        }}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`feedback-modal-icon ${feedbackModal.type}`}>
              {feedbackModal.type === 'success' ? '✓' : '!'}
            </div>
            <h2 className="feedback-modal-title">{feedbackModal.title}</h2>
            <p className="feedback-modal-message">{feedbackModal.message}</p>
            {feedbackModal.action && (
              <button 
                className="feedback-modal-cta"
                onClick={() => {
                  feedbackModal.action.onClick();
                  setFeedbackModal(null);
                }}
              >
                {feedbackModal.action.label}
              </button>
            )}
            {feedbackModal.dismissable !== false && (
              <button 
                className="feedback-modal-dismiss"
                onClick={() => setFeedbackModal(null)}
              >
                {feedbackModal.dismissLabel || 'Close'}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Inner component that has access to Router context
function AppContent({ railCollapsed, setRailCollapsed, data, setData, testSlackId, setTestSlackId, realDrift, setRealDrift, isLeadOrAdmin, toast, setToast, feedbackModal, setFeedbackModal, loadQuarters }) {
  const location = useLocation();
  const [tourActive, setTourActive] = useState(false);
  const [slackCommandsModalOpen, setSlackCommandsModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [tourCompleted, setTourCompleted] = useState(null); // null = not checked yet (may be used for future features)

  // Check tour completion status on mount - only if authenticated and not on login page
  useEffect(() => {
    const checkTourStatus = async () => {
      // Don't show tour on login page
      if (location.pathname === '/login') {
        return;
      }
      
      // Check if user is authenticated and enabled
      const status = await hasValidSession();
      if ((!status.authenticated || !status.enabled) && !ENV_CONFIG.DEV_MODE) {
        return; // Don't show tour if not authenticated/enabled (unless in dev mode)
      }
      
      try {
        const slackId = ENV_CONFIG.DEV_MODE ? testSlackId : null;
        const completed = await getTourCompleted(slackId);
        setTourCompleted(completed);
        // If not completed, show tour after a short delay
        if (!completed) {
          setTimeout(() => {
            setTourActive(true);
          }, 1000);
        }
      } catch (error) {
        console.error('Failed to check tour status:', error);
        // On error, don't show tour (fail silently)
        setTourCompleted(false);
      }
    };

    checkTourStatus();
  }, [testSlackId, location.pathname]);

  // Handle tour completion
  const handleTourComplete = async () => {
    setTourActive(false);
    try {
      const slackId = ENV_CONFIG.DEV_MODE ? testSlackId : null;
      await updateTourCompleted(true, slackId);
      setTourCompleted(true);
      console.log('Tour completion saved successfully');
    } catch (error) {
      console.error('Failed to update tour completion:', error);
      // Don't show alert - just log and continue (non-blocking)
      // User can still use the app even if tour completion doesn't save
    }
  };

  // Handle tour skip
  const handleTourSkip = async () => {
    setTourActive(false);
    try {
      const slackId = ENV_CONFIG.DEV_MODE ? testSlackId : null;
      await updateTourCompleted(true, slackId);
      setTourCompleted(true);
      console.log('Tour skip saved successfully');
    } catch (error) {
      console.error('Failed to update tour completion:', error);
    }
  };

  // Handle tour restart (from help icon)
  const handleTourRestart = () => {
    setTourActive(true);
  };

  return (
    <div className={`App ${tourActive ? 'tour-active' : ''}`}>
        {/* Left Rail */}
        <div className={`left-rail ${railCollapsed ? 'collapsed' : ''}`}>
          <div className="rail-header">
            <button 
              className="toggle-btn"
              onClick={() => setRailCollapsed(!railCollapsed)}
              data-tooltip={railCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {railCollapsed ? '→' : '←'}
            </button>
          </div>
          
          <nav className="rail-nav">
            <a href="/overview" className="nav-item" data-tooltip="Home">
              <span className="nav-icon" aria-label="Home" role="img">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </span>
              {!railCollapsed && <span className="nav-text">Home</span>}
            </a>
            <a href="/plan" className="nav-item" data-tooltip="My Plan" data-nav="plan">
              <span className="nav-icon" aria-label="My Plan" role="img">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </span>
              {!railCollapsed && <span className="nav-text">My Plan</span>}
            </a>
            <a href="/editor" className="nav-item" data-tooltip="OKR Editor" data-nav="editor">
              <span className="nav-icon" aria-label="OKR Editor" role="img">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                </svg>
              </span>
              {!railCollapsed && <span className="nav-text">OKR Editor</span>}
            </a>
            
            {/* Spacer to push profile to bottom */}
            <div style={{ flex: 1 }}></div>
            
            <a href="/profile" className="nav-item" data-tooltip="Profile">
              <span className="nav-icon" aria-label="Profile" role="img">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              {!railCollapsed && <span className="nav-text">Profile</span>}
            </a>
          </nav>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Header - Hidden on login page */}
          {location.pathname !== '/login' && (
          <header className="header">
            <div className="header-left">
              {/* Hide quarter switcher on OKR Editor page - it has its own in-context selector */}
              {location.pathname !== '/editor' && (
              <div className="quarter-switcher">
                <select 
                  value={data.currentQuarter} 
                  onChange={(e) => {
                    const newQuarter = e.target.value;
                    console.log('[QUARTER_SELECTOR] Changing quarter to:', newQuarter);
                    // Save to sessionStorage for persistence across page navigation
                    sessionStorage.setItem('selectedQuarter', newQuarter);
                    setData(prev => ({ ...prev, currentQuarter: newQuarter }));
                  }}
                >
                  {data.availableQuarters.map(quarter => (
                    <option key={quarter} value={quarter}>{quarter.replace('-', ' ')}</option>
                  ))}
                </select>
              </div>
              )}
            </div>

            <div className="header-right">
              {isLeadOrAdmin && (
                <div className="team-switcher">
                  <select defaultValue={mockSession.team}>
                    <option value="Engineering">Engineering</option>
                    <option value="Product">Product</option>
                    <option value="Design">Design</option>
                  </select>
                </div>
              )}

              {/* Help Icon - Tour & Slack Commands */}
              <button 
                className="help-icon" 
                onClick={() => setSlackCommandsModalOpen(true)}
                data-tooltip="Slack Commands"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </button>

              {/* Logout Button - Production Only */}
              {!ENV_CONFIG.DEV_MODE && location.pathname !== '/not-enabled' && (
                <button 
                  className="logout-button" 
                  onClick={async () => {
                    // Call backend logout endpoint to clear HttpOnly cookie
                    try {
                      await fetch(`${ENV_CONFIG.API_URL}/api/auth/logout`, {
                        method: 'POST',
                        credentials: 'include'
                      });
                    } catch (error) {
                      console.error('Logout error:', error);
                    }
                    // Clear session cache
                    sessionCheckCache.value = null;
                    sessionCheckCache.enabled = null;
                    sessionCheckCache.timestamp = 0;
                    // Redirect to login
                    window.location.href = '/login';
                  }}
                  data-tooltip="Sign Out"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </button>
              )}
            </div>
          </header>
          )}

          {/* Test Slack ID Input - Dev Mode Only */}
          {ENV_CONFIG.DEV_MODE && (
            <div className="test-slack-toolbar">
              <div>
                <label>Test Slack ID (Dev Mode)</label>
                <input 
                  type="text" 
                  value={testSlackId} 
                  onChange={(e) => setTestSlackId(e.target.value)}
                  placeholder="Enter Slack ID for testing"
                />
              </div>
              <div className="dev-mode-badge">Development Mode</div>
            </div>
          )}

          {/* Page Content */}
          <main className="page-content">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/not-enabled" element={<NotEnabledPage />} />
              <Route 
                path="/overview" 
                element={
                  <ProtectedRoute onAuthSuccess={loadQuarters}>
                    <Home testSlackId={testSlackId} setRealDrift={setRealDrift} setData={setData} currentQuarter={data.currentQuarter} />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/plan" 
                element={
                  <ProtectedRoute onAuthSuccess={loadQuarters}>
                    <MyPlan key={data.currentQuarter} testSlackId={testSlackId} currentQuarter={data.currentQuarter} setToast={setToast} />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/editor" 
                element={
                  <ProtectedRoute onAuthSuccess={loadQuarters}>
                    <OKREditor testSlackId={testSlackId} setToast={setToast} setFeedbackModal={setFeedbackModal} currentQuarter={data.currentQuarter} />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute onAuthSuccess={loadQuarters}>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route path="/" element={<Navigate to="/overview" replace />} />
            </Routes>
          </main>
        </div>

        {/* Onboarding Tour */}
        <OnboardingTour 
          isActive={tourActive}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />

        {/* Slack Commands Modal */}
        <SlackCommandsModal 
          isOpen={slackCommandsModalOpen}
          onClose={() => setSlackCommandsModalOpen(false)}
          onReplayTour={handleTourRestart}
        />

        {/* Feedback Widget (hidden during tour) */}
        <FeedbackWidget testSlackId={testSlackId} setToast={setToast} isHidden={tourActive} />
    </div>
  );
}

// Overview Page
// Zen Home Component - Apple-style calm focus experience
function Home({ testSlackId, setRealDrift, setData, currentQuarter }) {
  const [homeData, setHomeData] = useState({ drift: 0, driftBreakdown: null, suggestions: [], loading: true, hasGoals: true, hasLinkedActivity: false });
  // eslint-disable-next-line no-unused-vars
  const [showToast, setShowToast] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [toastMessage, setToastMessage] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [showNextStep, setShowNextStep] = useState(false);
  const [showDriftTooltip, setShowDriftTooltip] = useState(false);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [showGoalsHelpModal, setShowGoalsHelpModal] = useState(false);
  
  // Cycle to next suggestion (stays expanded within session)
  const cycleToNextSuggestion = () => {
    const nextIndex = (currentSuggestionIndex + 1) % homeData.suggestions.length;
    setCurrentSuggestionIndex(nextIndex);
    // Keep expanded=true so user doesn't need to re-click
    console.log('[TELEMETRY] home_suggestion_next', { fromIndex: currentSuggestionIndex, toIndex: nextIndex });
  };
  
  // Load home data
  const loadHome = useCallback(async () => {
    try {
      const quarter = currentQuarter || mockData.currentQuarter;
      const slackId = testSlackId; // In dev mode, must enter a Slack ID; in prod, uses session
      const cacheKey = `home_drift_${quarter}_${slackId}`;
      
      // Check sessionStorage cache first (30s TTL for instant revisits)
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < 30000) { // 30s cache
            console.log('[HOME_CACHE] Using cached data, age:', age, 'ms');
            setHomeData({ 
              drift: data.drift || 0, 
              driftBreakdown: data.driftBreakdown,
              suggestions: data.suggestions || [], 
              hasGoals: data.hasGoals !== false,
              hasLinkedActivity: data.hasLinkedActivity === true,
              loading: false,
              // Preserve v2 fields if present
              scoreVersion: data.scoreVersion,
              alignmentScore: data.alignmentScore,
              alignmentBreakdown: data.alignmentBreakdown,
              momentumActiveDays7d: data.momentumActiveDays7d,
              hasScoreData: data.hasScoreData
            });
            if (setRealDrift) {
              setRealDrift(data.drift || 0);
            }
            // Update App-level data with v2 fields for header display
            if (setData) {
              setData(prev => ({
                ...prev,
                scoreVersion: data.scoreVersion,
                alignmentScore: data.alignmentScore,
                alignmentBreakdown: data.alignmentBreakdown,
                momentumActiveDays7d: data.momentumActiveDays7d,
                hasScoreData: data.hasScoreData
              }));
            }
            setTimeout(() => setShowNextStep(true), 600);
            return; // Skip API call
          }
        } catch (e) {
          console.warn('[HOME_CACHE] Cache parse error:', e);
        }
      }
      
      setHomeData(prev => ({ ...prev, loading: true }));
      setShowNextStep(false);
      setCurrentSuggestionIndex(0);
      
      const data = await getHomeSuggestion(quarter, slackId);
      
      // Cache the result
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('[HOME_CACHE] Failed to cache:', e);
      }
      
      setHomeData({ 
        drift: data.drift || 0, 
        driftBreakdown: data.driftBreakdown,
        suggestions: data.suggestions || [], 
        hasGoals: data.hasGoals !== false, // Default to true if not provided (backward compat)
        hasLinkedActivity: data.hasLinkedActivity === true, // Default to false if not provided
        loading: false,
        // Preserve v2 fields if present
        scoreVersion: data.scoreVersion,
        alignmentScore: data.alignmentScore,
        alignmentBreakdown: data.alignmentBreakdown,
        momentumActiveDays7d: data.momentumActiveDays7d,
        hasScoreData: data.hasScoreData
      });
      
      // Update App-level drift for header display
      if (setRealDrift) {
        setRealDrift(data.drift || 0);
      }
      // Update App-level data with v2 fields for header display
      if (setData) {
        setData(prev => ({
          ...prev,
          scoreVersion: data.scoreVersion,
          alignmentScore: data.alignmentScore,
          alignmentBreakdown: data.alignmentBreakdown,
          momentumActiveDays7d: data.momentumActiveDays7d,
          hasScoreData: data.hasScoreData
        }));
      }
      
      // Staggered reveal: show next step after 600ms
      setTimeout(() => setShowNextStep(true), 600);
    } catch (error) {
      console.error('Failed to load home data:', error);
      setHomeData({ 
        drift: 0, 
        driftBreakdown: null,
        suggestions: [{ type: 'calm', id: null, title: null, prompt: 'Everything is in balance' }], 
        hasGoals: true, // Default to true on error (assume user has goals)
        hasLinkedActivity: false, // Default to false on error
        loading: false 
      });
      
      // Update App-level drift even on error (fallback to 0)
      if (setRealDrift) {
        setRealDrift(0);
      }
      setTimeout(() => setShowNextStep(true), 600);
    }
  }, [testSlackId, setRealDrift, setData, currentQuarter]);
  
  useEffect(() => {
    loadHome();
  }, [loadHome]);
  
  // Alignment/Drift color mapping - modern, luxurious zen palette (4 tiers)
  const getScoreColor = (score, isV2) => {
    if (score === null || score === undefined) return '#E2E8F0'; // No score: cool gray
    
    if (isV2) {
      // Drift v2: Lower is better (matches tooltip ranges)
      if (score <= 25) return '#2F7F73'; // Very low drift: deep calm teal
      if (score <= 45) return '#63B7A6'; // Some drift: soft teal
      if (score <= 65) return '#D6B36A'; // Rising drift: champagne gold
      return '#B76A7A'; // High drift: muted rose
    } else {
      // Drift v1: Lower is better (legacy)
      if (score <= 10) return '#8BC9BD';
      if (score <= 30) return '#B8C480';
      if (score <= 60) return '#D9B68C';
      if (score <= 80) return '#D48A7E';
      return '#C97A7A';
    }
  };
  
  // Determine if we're using v2 (alignment) or v1 (drift)
  const isV2 = homeData.scoreVersion === 'v2';
  const displayScore = isV2 ? (homeData.alignmentScore !== null ? (100 - homeData.alignmentScore) : null) : homeData.drift;
  const hasScore = displayScore !== null;
  const scoreColor = getScoreColor(displayScore, isV2);
  
  // Always show full ring; color alone indicates status
  const circumference = 2 * Math.PI * 137; // radius = 137 (for 320px SVG)
  const strokeDashoffset = 0; // Full ring always
  
  // Get score explanation text for tooltip (v2: concise and actionable)
  const getScoreExplanation = () => {
    if (isV2) {
      // Alignment v2 explanation
      if (!hasScore) {
        return 'No estimate yet — capture and link a few tasks to OKRs.';
      }
      
      return `Drift measures how much work is disconnected from your goals.

Lower drift = more focus.

0–25  → Very low drift — great shape
26–45 → Some drift — worth tightening
46–65 → Drift rising — time to refocus
66+   → High drift — priorities may be slipping`;
    } else {
      // Drift v1 explanation (legacy)
      if (!homeData.drift && homeData.drift !== 0) return 'Loading...';
      
      const drift = homeData.drift;
      
      // Build explanation with formula and status
      let explanation = 'Lower drift = more focus.\n\n';
      
      if (drift <= 15) {
        explanation += '0–15  → Very low drift\n';
        explanation += 'Strong alignment. Keep going.';
      } else if (drift <= 40) {
        explanation += '16–40 → Some drift\n';
        explanation += 'A few tasks aren\'t tied to priorities.';
      } else {
        explanation += '41+   → High drift\n';
        explanation += 'Work is spreading away from goals.';
      }
      
      explanation += '\n\nDrift Formula:\n';
      explanation += '+10 per unlinked objective\n';
      explanation += '+5 per stale KR (no progress in 7 days)\n';
      explanation += '+3 per overdue task';
      
      return explanation;
    }
  };
  
  // Get current suggestion from array
  const getCurrentSuggestion = () => {
    if (!homeData.suggestions || homeData.suggestions.length === 0) return null;
    return homeData.suggestions[currentSuggestionIndex] || homeData.suggestions[0];
  };
  
  // Get next step content
  const getNextStepContent = () => {
    const suggestion = getCurrentSuggestion();
    if (!suggestion) return null;
    
    const { type, title, count, prompt } = suggestion;
    
    const nextStepConfig = {
      unlinkedTasks: {
        prompt: "Here's your next move.",
        detail: prompt || `You have ${count} unlinked tasks`,
        primaryAction: 'Link tasks',
        primaryHandler: () => window.location.href = '/plan',
        altAction: 'View tasks',
        altHandler: () => window.location.href = '/plan'
      },
      neglectedKR: {
        prompt: 'Take the next step.',
        detail: prompt || `No recent progress on ${title}`,
        primaryAction: 'Update KR',
        primaryHandler: () => window.location.href = '/plan',
        altAction: 'Add tasks',
        altHandler: () => window.location.href = '/plan'
      },
      nearCompleteKR: {
        prompt: 'Your focus today.',
        detail: prompt || `Finish ${title} strong`,
        primaryAction: 'Update progress',
        primaryHandler: () => window.location.href = '/plan',
        altAction: 'Add task',
        altHandler: () => window.location.href = '/plan'
      },
      highDrift: {
        prompt: 'Your focus today.',
        detail: prompt || 'High drift - review your work alignment',
        primaryAction: 'Review alignment',
        primaryHandler: () => window.location.href = '/plan',
        altAction: null,
        altHandler: null
      },
      calm: {
        prompt: 'Your focus today.',
        detail: 'Everything is in balance',
        primaryAction: 'View My Plan',
        primaryHandler: () => window.location.href = '/plan',
        altAction: null,
        altHandler: null
      }
    };
    
    return nextStepConfig[type] || null;
  };
  
  const nextStep = getNextStepContent();
  
  if (homeData.loading) {
    return (
      <div className="home-zen-v2">
        <div className="zen-container">
          <div className="drift-hero">
            <div className="drift-ring-skeleton" />
          </div>
        </div>
      </div>
    );
  }
  
  // State 1: No goals for current quarter
  console.log('[HOME DEBUG] Rendering - hasGoals:', homeData.hasGoals, 'hasLinkedActivity:', homeData.hasLinkedActivity, 'loading:', homeData.loading);
  if (!homeData.hasGoals) {
    console.log('[HOME DEBUG] Rendering State 1: No goals');
    return (
      <div className="home-zen-v2">
        <div className="zen-container">
          <div className="drift-hero">
            <div className="drift-ring-container-v2">
              <svg width="320" height="320" viewBox="0 0 320 320" aria-label="No drift score yet">
                {/* Background circle - neutral gray, no animation */}
                <circle
                  cx="160"
                  cy="160"
                  r="137"
                  fill="none"
                  stroke="#F1F5F9"
                  strokeWidth="10"
                />
                {/* No progress circle - just empty ring */}
              </svg>
              <div className="drift-value-v2">
                <span className="drift-number-v2" style={{ color: '#94A3B8' }}>—</span>
                <span className="drift-label-v2" style={{ color: '#94A3B8' }}>Drift Score</span>
              </div>
            </div>
          </div>
          
          {/* Empty State Content */}
          <div className="empty-state-content" style={{ 
            textAlign: 'center', 
            marginTop: '48px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px'
          }}>
            <p style={{ 
              fontSize: '18px', 
              color: '#475569', 
              margin: 0,
              fontWeight: 400
            }}>
              Set goals to enable Focus & Drift.
            </p>
            
            <button
              onClick={() => window.location.href = '/editor'}
              style={{
                background: '#475569',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
              onMouseEnter={(e) => e.target.style.background = '#334155'}
              onMouseLeave={(e) => e.target.style.background = '#475569'}
            >
              Create your first goals
            </button>
            
            <button
              onClick={() => setShowGoalsHelpModal(true)}
              style={{
                fontSize: '14px',
                color: '#64748B',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
              onMouseEnter={(e) => e.target.style.color = '#475569'}
              onMouseLeave={(e) => e.target.style.color = '#64748B'}
            >
              Learn how DecisionOS structures goals
            </button>
          </div>
          
          {/* Goals Help Modal */}
          {showGoalsHelpModal && (
            <div className="slack-commands-modal" onClick={() => setShowGoalsHelpModal(false)}>
              <div className="slack-commands-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="slack-commands-modal-header">
                  <h2>How DecisionOS Structures Goals</h2>
                  <button className="slack-commands-modal-close" onClick={() => setShowGoalsHelpModal(false)} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                
                <div className="slack-commands-modal-body" style={{ padding: '24px' }}>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '12px' }}>Objectives & Key Results (OKRs)</h3>
                    <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '16px' }}>
                      DecisionOS uses the OKR framework to help you stay focused and aligned:
                    </p>
                    <ul style={{ fontSize: '14px', color: '#475569', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '16px' }}>
                      <li><strong>Objectives</strong> are qualitative, inspirational goals that define what you want to achieve.</li>
                      <li><strong>Key Results</strong> are measurable outcomes that track progress toward your objective.</li>
                    </ul>
                  </div>
                  
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '12px' }}>Example</h3>
                    <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '8px', fontSize: '14px', color: '#475569' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <strong style={{ color: '#0F172A' }}>Objective:</strong> Ship a game-changing feature
                      </div>
                      <div style={{ paddingLeft: '16px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: '#0F172A' }}>Key Result 1:</strong> Complete user research with 20+ participants
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: '#0F172A' }}>Key Result 2:</strong> Launch beta to 100 users
                        </div>
                        <div>
                          <strong style={{ color: '#0F172A' }}>Key Result 3:</strong> Achieve 80% satisfaction score
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #E2E8F0', fontSize: '13px', color: '#64748B' }}>
                    <strong>Tip:</strong> Start with 1-3 objectives per quarter, each with 2-4 key results. Keep them focused and achievable.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // State 2: Goals exist but no linked activity
  if (homeData.hasGoals && !homeData.hasLinkedActivity) {
    console.log('[HOME DEBUG] Rendering State 2: Goals but no linked activity');
    return (
      <div className="home-zen-v2">
        <div className="zen-container">
          <div className="drift-hero">
            <div className="drift-ring-container-v2">
              <svg width="320" height="320" viewBox="0 0 320 320" aria-label="No drift score yet">
                {/* Background circle - neutral gray, no animation */}
                <circle
                  cx="160"
                  cy="160"
                  r="137"
                  fill="none"
                  stroke="#F1F5F9"
                  strokeWidth="10"
                />
                {/* No progress circle - just empty ring */}
              </svg>
              <div className="drift-value-v2">
                <span className="drift-number-v2" style={{ color: '#94A3B8' }}>—</span>
                <span className="drift-label-v2" style={{ color: '#94A3B8' }}>Drift Score</span>
              </div>
            </div>
          </div>
          
          {/* Activate Plan State Content */}
          <div className="empty-state-content" style={{ 
            textAlign: 'center', 
            marginTop: '48px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px'
          }}>
            <p style={{ 
              fontSize: '18px', 
              color: '#475569', 
              margin: 0,
              fontWeight: 400
            }}>
              Activate your plan
            </p>
            
            <button
              onClick={() => window.location.href = '/plan'}
              style={{
                background: '#475569',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
              onMouseEnter={(e) => e.target.style.background = '#334155'}
              onMouseLeave={(e) => e.target.style.background = '#475569'}
            >
              Link a task or add your first capture
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // State 3: Goals + linked activity present → show normal drift score and next step
  console.log('[HOME DEBUG] Rendering State 3: Goals + linked activity');
  
  // Normal state: User has goals
  return (
    <div className="home-zen-v2">
      <div className="zen-container">
        {/* Drift Hero Section */}
        <div className="drift-hero">
          <div 
            className="drift-ring-container-v2"
            data-tour="drift-score"
            onMouseEnter={() => setShowDriftTooltip(true)}
            onMouseLeave={() => setShowDriftTooltip(false)}
          >
            <svg width="320" height="320" viewBox="0 0 320 320" aria-label={hasScore ? `${isV2 ? 'Alignment' : 'Drift'} score ${displayScore} out of 100` : 'No score yet'}>
              {/* Glow effect */}
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              {/* Background circle */}
              <circle
                cx="160"
                cy="160"
                r="137"
                fill="none"
                stroke="#F1F5F9"
                strokeWidth="10"
              />
              {/* Progress circle with glow - full ring, color indicates status */}
              {hasScore && (
              <circle
                cx="160"
                cy="160"
                  r="137"
                fill="none"
                  stroke={scoreColor}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 160 160)"
                filter="url(#glow)"
                style={{ 
                  transition: 'stroke 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: 0.85
                }}
              />
              )}
            </svg>
            <div className="drift-value-v2">
              <span className="drift-number-v2" style={!hasScore ? { color: '#94A3B8' } : {}}>
                {hasScore ? displayScore : '—'}
              </span>
              <span className="drift-label-v2" style={{
                ...(hasScore ? {} : { color: '#94A3B8' }),
                fontSize: '16px',
                fontWeight: '600',
                letterSpacing: '0.05em'
              }}>
                DRIFT SCORE
              </span>
              {/* Explanation line: simple, direct connection to the score */}
              {isV2 && hasScore && homeData.driftBreakdown && (
                <span style={{
                  display: 'block',
                  fontSize: '13px',
                  color: '#64748B',
                  marginTop: '10px',
                  fontWeight: 400,
                  lineHeight: '1.4'
                }}>
                  {homeData.driftBreakdown.linkedRecentCaptures14d} of {homeData.driftBreakdown.totalRecentCaptures14d} tasks linked
                </span>
              )}
              {isV2 && !hasScore && (
                <span style={{
                  display: 'block',
                  fontSize: '13px',
                  color: '#94A3B8',
                  marginTop: '10px',
                  fontWeight: 400
                }}>
                  No estimate yet
                </span>
              )}
            </div>
            
            {/* Score Tooltip */}
            {showDriftTooltip && (
              <div className="drift-tooltip" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                {getScoreExplanation()}
              </div>
            )}
          </div>
        </div>
        
        {/* Next Step Element */}
        {showNextStep && nextStep && (
          <div className={`next-step-element ${expanded ? 'expanded' : ''}`} data-tour="next-step">
            {!expanded ? (
              <button 
                className="next-step-trigger"
                onClick={() => setExpanded(true)}
              >
                <span className="next-step-prompt">{nextStep.prompt}</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="chevron-icon">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ) : (
              <div className="next-step-expanded">
                <div className="next-step-content">
                  <p className="next-step-detail">{nextStep.detail}</p>
                  <div className="next-step-actions">
                    <button 
                      className="primary-action"
                      onClick={nextStep.primaryHandler}
                    >
                      {nextStep.primaryAction}
                    </button>
                    {nextStep.altAction && (
                      <button 
                        className="alt-action"
                        onClick={nextStep.altHandler}
                      >
                        {nextStep.altAction}
                      </button>
                    )}
                  </div>
                </div>
                {homeData.suggestions && homeData.suggestions.length > 1 && (
                  <button 
                    className="next-suggestion-btn"
                    onClick={cycleToNextSuggestion}
                  >
                    Next →
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Tagline */}
      <p className="zen-tagline-v2">DecisionOS — helping you take the next step</p>
      
      {/* Toast */}
      {showToast && (
        <div className="home-toast-v2">
          {toastMessage}
        </div>
      )}
    </div>
  );
}

// ========================================
// NOTE: Legacy Overview component replaced by Zen Home above
// Old Overview code available in git history if rollback needed
// ========================================

// My Plan Page
function MyPlan({ testSlackId, currentQuarter, setToast }) {
  console.log('[MY_PLAN] Component rendered with currentQuarter:', currentQuarter);
  const [myPlanData, setMyPlanData] = useState({
    objectives: [],
    unlinkedTasks: [],
    captures: [], // All open captures for This Week's Focus
    loading: true,
    error: null
  });

  const [expandedObjectiveIds, setExpandedObjectiveIds] = useState(new Set());
  const [linkKRModalOpen, setLinkKRModalOpen] = useState(false);
  const [taskToLink, setTaskToLink] = useState(null);
  const lastRefreshRef = useRef(0); // Track last refresh time for throttling
  
  // Inline edit state
  const [editingObjectiveId, setEditingObjectiveId] = useState(null);
  const [editFormData, setEditFormData] = useState(null); // { name, quarter, keyResults: [...] }
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const editOriginalRef = useRef(null);
  const defaultNewKRForm = React.useMemo(() => ({ 
    type: 'metric', text: '', target: '', unit: '', polarity: 'up' 
  }), []);
  const [newKRForm, setNewKRForm] = useState(defaultNewKRForm);
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, item: null, krCount: 0 });

  // Reusable refresh function - fetches fresh data from server
  const refreshMyPlan = useCallback(async (showLoading = false) => {
      try {
      if (showLoading) {
        setMyPlanData(prev => ({ ...prev, loading: true, error: null }));
      }
        
        const quarter = currentQuarter || mockData.currentQuarter;
        const slackId = ENV_CONFIG.DEV_MODE ? testSlackId : null;
        
      console.log('[MY_PLAN] Refreshing data for quarter:', quarter);
        const data = await getMyPlan(quarter, slackId);
        
        setMyPlanData({
          objectives: data.objectives || [],
          unlinkedTasks: data.unlinkedTasks || [],
        captures: data.captures || [],
          loading: false,
          error: null
        });
      
      lastRefreshRef.current = Date.now();
      console.log('[MY_PLAN] Data refreshed successfully');
      } catch (error) {
      console.error('Failed to refresh my plan data:', error);
        setMyPlanData(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
    }
  }, [testSlackId, currentQuarter]);

  // Clear home cache (drift data) when captures change
  const clearHomeCache = useCallback(() => {
    // Clear all home_drift cache keys from sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('home_drift_')) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  // Shared delete handler (used by both This Week's Focus and Unlinked Tasks)
  const handleDeleteTask = useCallback(async (taskId) => {
    try {
      await deleteCapture(taskId, testSlackId);
      // Clear home cache and refresh to get updated data
      clearHomeCache();
      await refreshMyPlan(false);
      if (setToast) {
        setToast('Task deleted');
        setTimeout(() => setToast(null), 2000);
      }
    } catch (error) {
      console.error('Delete task error:', error);
      if (setToast) {
        setToast(`Failed to delete task: ${error.message}`);
        setTimeout(() => setToast(null), 4000);
      }
    }
  }, [testSlackId, clearHomeCache, refreshMyPlan, setToast]);

  // Generate quarter options (current + 3 future)
  const getQuarterOptions = useCallback(() => {
    const current = currentQuarter || '2026-Q1';
    const [year, q] = current.split('-Q');
    const quarters = [];
    let y = parseInt(year);
    let qNum = parseInt(q);
    for (let i = 0; i < 4; i++) {
      quarters.push(`${y}-Q${qNum}`);
      qNum++;
      if (qNum > 4) { qNum = 1; y++; }
    }
    return quarters;
  }, [currentQuarter]);

  // Handle entering edit mode for an objective
  const handleStartEdit = useCallback((obj) => {
    const normalizeTargetValue = (value) => (value === null || value === undefined ? '' : String(value));
    setEditingObjectiveId(obj.id);
    setEditFormData({
      name: obj.name,
      quarter: obj.quarter,
      keyResults: (obj.keyResults || []).map(kr => ({
        id: kr.id,
        text: kr.text,
        target: normalizeTargetValue(kr.target),
        type: kr.type || 'Milestone',
        unit: kr.unit || '',
        polarity: kr.polarity || 'Higher is better'
      }))
    });
    editOriginalRef.current = {
      name: obj.name,
      quarter: obj.quarter,
      keyResults: (obj.keyResults || []).map(kr => ({
        id: kr.id,
        text: kr.text,
        target: normalizeTargetValue(kr.target),
        type: kr.type || 'Milestone',
        unit: kr.unit || '',
        polarity: kr.polarity || 'Higher is better'
      }))
    };
    setNewKRForm(defaultNewKRForm);
    // Auto-expand the objective
    setExpandedObjectiveIds(prev => new Set([...prev, obj.id]));
  }, [defaultNewKRForm]);

  // Handle canceling edit
  const handleCancelEdit = useCallback(() => {
    setEditingObjectiveId(null);
    setEditFormData(null);
    editOriginalRef.current = null;
    setNewKRForm(defaultNewKRForm);
  }, [defaultNewKRForm]);

  const handleAddKRToEdit = useCallback(() => {
    if (!editFormData) return;
    const text = (newKRForm.text || '').trim();
    if (!text) {
      if (setToast) {
        setToast('Key result text is required');
        setTimeout(() => setToast(null), 3000);
      }
      return;
    }
    if (newKRForm.type === 'metric') {
      if (newKRForm.target === '' || newKRForm.target === null || newKRForm.target === undefined) {
        if (setToast) {
          setToast('Target is required for metrics');
          setTimeout(() => setToast(null), 3000);
        }
        return;
      }
      if (Number.isNaN(Number(newKRForm.target))) {
        if (setToast) {
          setToast('Target must be a number');
          setTimeout(() => setToast(null), 3000);
        }
        return;
      }
    }

    const newKR = {
      id: `new-${Date.now()}`,
      text,
      target: newKRForm.type === 'metric' ? String(newKRForm.target) : '',
      type: newKRForm.type === 'metric' ? 'Metric' : 'Milestone',
      unit: newKRForm.type === 'metric' ? (newKRForm.unit || '') : '',
      polarity: newKRForm.type === 'metric' ? (newKRForm.polarity || 'Higher is better') : 'Higher is better',
      isNew: true
    };

    setEditFormData(prev => ({
      ...prev,
      keyResults: [...(prev?.keyResults || []), newKR]
    }));
    setNewKRForm(defaultNewKRForm);
  }, [editFormData, newKRForm, setToast, defaultNewKRForm]);

  // Handle saving edit (objective + all KRs in one action)
  const handleSaveEdit = useCallback(async (objectiveId) => {
    if (!editFormData) return;
    setIsSavingEdit(true);
    
    try {
      const normalizeTargetValue = (value) => (value === null || value === undefined ? '' : String(value));
      const mapPolarity = (value) => {
        if (!value) return 'Higher is better';
        if (value === 'up') return 'Higher is better';
        if (value === 'down') return 'Lower is better';
        return value;
      };
      const original = editOriginalRef.current || { name: '', quarter: '', keyResults: [] };
      const originalKRsById = new Map((original.keyResults || []).map(kr => [kr.id, kr]));

      const emptyText = (editFormData.keyResults || []).find(kr => !kr.text || !kr.text.trim());
      if (emptyText) {
        if (setToast) {
          setToast('Key result text is required');
          setTimeout(() => setToast(null), 4000);
        }
        return;
      }
      
      const invalidTarget = (editFormData.keyResults || []).find(kr => {
        if (kr.target === '' || kr.target === null || kr.target === undefined) return false;
        return Number.isNaN(Number(kr.target));
      });
      if (invalidTarget) {
        if (setToast) {
          setToast(`Invalid target for KR: ${invalidTarget.text}`);
          setTimeout(() => setToast(null), 4000);
        }
        return;
      }
      
      // Update objective
      const objectiveUpdates = {};
      if (editFormData.name !== original.name) objectiveUpdates.name = editFormData.name;
      if (editFormData.quarter !== original.quarter) objectiveUpdates.quarter = editFormData.quarter;
      if (Object.keys(objectiveUpdates).length > 0) {
        await updateObjective(objectiveId, objectiveUpdates, testSlackId);
      }
      
      // Update all KRs
      const existingKRs = (editFormData.keyResults || []).filter(kr => !kr.isNew);
      const newKRs = (editFormData.keyResults || []).filter(kr => kr.isNew);
      const krUpdates = existingKRs.map(kr => {
        const originalKR = originalKRsById.get(kr.id) || {};
        const updates = {};
        if (kr.text !== originalKR.text) updates.text = kr.text;
        if (normalizeTargetValue(kr.target) !== normalizeTargetValue(originalKR.target)) {
          updates.target = kr.target === '' ? null : kr.target;
        }
        return { id: kr.id, updates };
      }).filter(item => Object.keys(item.updates).length > 0);
      
      if (krUpdates.length > 0) {
        const results = await Promise.allSettled(
          krUpdates.map(kr => updateKR(kr.id, kr.updates, testSlackId))
        );
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          throw new Error(`Failed to update ${failed.length} key result${failed.length > 1 ? 's' : ''}`);
        }
      }

      if (newKRs.length > 0) {
        const createResults = await Promise.allSettled(
          newKRs.map(kr => createKR({
            objectiveId,
            text: kr.text,
            type: kr.type,
            target: kr.type === 'Metric' ? kr.target : null,
            unit: kr.type === 'Metric' ? kr.unit : null,
            polarity: kr.type === 'Metric' ? mapPolarity(kr.polarity) : null
          }, testSlackId))
        );
        const failedCreates = createResults.filter(r => r.status === 'rejected');
        if (failedCreates.length > 0) {
          throw new Error(`Failed to add ${failedCreates.length} key result${failedCreates.length > 1 ? 's' : ''}`);
        }
      }
      
      setToast('Changes saved');
      setTimeout(() => setToast(null), 2000);
      
      // Exit edit mode and refresh
      setEditingObjectiveId(null);
      setEditFormData(null);
      editOriginalRef.current = null;
      refreshMyPlan(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
      setToast(`Failed to save: ${error.message}`);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editFormData, testSlackId, setToast, refreshMyPlan]);

  // Handle delete confirmation
  const handleConfirmDelete = useCallback(async () => {
    const { type, item } = deleteModal;
    setDeleteModal({ isOpen: false, type: null, item: null, krCount: 0 });
    
    try {
      if (type === 'objective') {
        // Delete KRs first, then objective
        const krs = item.keyResults || [];
        for (const kr of krs) {
          await deleteKR(kr.id, testSlackId);
        }
        await deleteObjective(item.id, testSlackId);
        setToast('Objective deleted');
      } else if (type === 'kr') {
        await deleteKR(item.id, testSlackId);
        // If we're in edit mode, remove the KR from edit form data
        if (editFormData) {
          setEditFormData(prev => ({
            ...prev,
            keyResults: prev.keyResults.filter(kr => kr.id !== item.id)
          }));
        }
        setToast('Key result deleted');
      }
      setTimeout(() => setToast(null), 2000);
      refreshMyPlan(false);
    } catch (error) {
      console.error('Failed to delete:', error);
      setToast(`Failed to delete: ${error.message}`);
      setTimeout(() => setToast(null), 4000);
    }
  }, [deleteModal, testSlackId, editFormData, setToast, refreshMyPlan]);

  // Initial load
  useEffect(() => {
    refreshMyPlan(true);
  }, [refreshMyPlan]);

  // Refetch on window focus (throttled to 15s)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshRef.current;
        // Only refresh if more than 15s since last refresh
        if (timeSinceLastRefresh > 15000) {
          console.log('[MY_PLAN] Tab became visible, refreshing data...');
          refreshMyPlan(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshMyPlan]);

  if (myPlanData.loading) {
  return (
    <div className="page">
      <h1>My Plan</h1>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your plan...</p>
        </div>
      </div>
    );
  }

  if (myPlanData.error) {
    return (
      <div className="page">
        <h1>My Plan</h1>
        <div className="error-state">
          <p>❌ Failed to load your plan: {myPlanData.error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const { objectives, unlinkedTasks, captures } = myPlanData;

  return (
    <div className="page my-plan-content">
      <LinkToKRModal
        isOpen={linkKRModalOpen}
        onClose={() => {
          setLinkKRModalOpen(false);
          setTaskToLink(null);
        }}
        keyResults={(objectives||[]).flatMap(o => o.keyResults || [])}
        taskName={taskToLink?.text}
        onLink={async (kr) => {
          try {
            await linkCapture(taskToLink.id, kr.id, testSlackId);
            // Optimistic UI update - remove from unlinked, update in captures with krTitle
            setMyPlanData(prev => ({
              ...prev,
              unlinkedTasks: prev.unlinkedTasks.filter(t => t.id !== taskToLink.id),
              captures: prev.captures.map(t => 
                t.id === taskToLink.id 
                  ? { ...t, krTitle: kr.text } 
                  : t
              )
            }));
            if (setToast) {
              setToast('Task linked');
              setTimeout(() => setToast(null), 3000);
            }
            // Clear home cache (linking affects drift) and refresh to get server state
            clearHomeCache();
            refreshMyPlan(false);
          } catch (e) {
            if (setToast) {
              setToast(`Failed to link: ${e.message}`);
              setTimeout(() => setToast(null), 4000);
            }
          }
        }}
      />
      
      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ isOpen: false, type: null, item: null, krCount: 0 })}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p className="delete-confirm-message">
              {deleteModal.type === 'objective' 
                ? `This will delete "${deleteModal.item?.name}" and its ${deleteModal.krCount} key result${deleteModal.krCount !== 1 ? 's' : ''}. This action cannot be undone.`
                : `This will delete "${deleteModal.item?.text}". This action cannot be undone.`
              }
            </p>
            <div className="delete-confirm-actions">
              <button 
                className="delete-confirm-cancel"
                onClick={() => setDeleteModal({ isOpen: false, type: null, item: null, krCount: 0 })}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-delete"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 style={{ marginBottom: '2rem' }}>My Plan</h1>
      {/* Section 1: This Week's Focus */}
      <ThisWeeksFocus
        captures={captures}
        setToast={setToast}
        onAddTask={async (title) => {
          if (!title) return;
          try {
            const result = await createCapture(title, testSlackId);
            if (result && result.capture) {
              // Optimistic UI update
              const newCapture = { 
                    id: result.capture.id, 
                    text: result.capture.text, 
                    dueDate: result.capture.dueDate ? result.capture.dueDate.slice(0,10) : null,
                    snoozeUntil: result.capture.snoozeUntil || null,
                status: 'open',
                krTitle: null
              };
              setMyPlanData(prev => ({
                ...prev,
                unlinkedTasks: [...prev.unlinkedTasks, newCapture],
                captures: [...prev.captures, newCapture]
              }));
              // Clear home cache (new capture affects drift) and refresh
              clearHomeCache();
              refreshMyPlan(false);
            }
          } catch (error) {
            console.error('Failed to create task:', error);
            // Re-throw to let modal handle the error
            throw error;
          }
        }}
        onMarkComplete={async (taskId) => {
          try {
            await updateCapture(taskId, { status: 'Done' }, testSlackId);
            // Optimistic UI update - remove from both lists
            setMyPlanData(prev => ({
              ...prev,
              unlinkedTasks: prev.unlinkedTasks.filter(t => t.id !== taskId),
              captures: prev.captures.filter(t => t.id !== taskId)
            }));
            if (setToast) {
              setToast('Task completed');
              setTimeout(() => setToast(null), 3000);
            }
            // Clear home cache and refresh
            clearHomeCache();
            refreshMyPlan(false);
          } catch (error) {
            console.error('Failed to complete task:', error);
            if (setToast) {
              setToast(`Failed to complete task: ${error.message}`);
              setTimeout(() => setToast(null), 4000);
            }
          }
        }}
        onDeleteTask={handleDeleteTask}
        onLinkToKR={(taskId) => {
          const allKRs = (objectives||[]).flatMap(o => o.keyResults || []);
          if (allKRs.length === 0) { 
            if (setToast) {
              setToast('No KRs available to link');
              setTimeout(() => setToast(null), 3000);
            }
            return; 
          }
          // Look in captures array (This Week's Focus uses all captures, not just unlinked)
          const task = captures.find(t => t.id === taskId) || unlinkedTasks.find(t => t.id === taskId);
          setTaskToLink(task);
          setLinkKRModalOpen(true);
        }}
        onEditDate={async (taskId, newDate) => {
          try {
            await updateCapture(taskId, { due_date: newDate }, testSlackId);
            // Optimistic UI update
            setMyPlanData(prev => ({
              ...prev,
              unlinkedTasks: prev.unlinkedTasks.map(t => 
                t.id === taskId ? { ...t, dueDate: newDate } : t
              ),
              captures: prev.captures.map(t => 
                t.id === taskId ? { ...t, dueDate: newDate } : t
              )
            }));
            if (setToast) {
              setToast(newDate ? 'Date updated' : 'Date cleared');
              setTimeout(() => setToast(null), 3000);
            }
            // Clear home cache and refresh data
            clearHomeCache();
            refreshMyPlan(false);
          } catch (error) {
            console.error('Failed to update date:', error);
            if (setToast) {
              setToast(`Failed to update date: ${error.message}`);
              setTimeout(() => setToast(null), 4000);
            }
          }
        }}
      />
      
      {/* Section 2: My OKRs (collapsible) */}
      <div className="section-card okrs-card my-okrs-section">
        <h2>My OKRs ({currentQuarter || mockData.currentQuarter})</h2>
        {objectives.length > 0 ? (
          <div className="objectives-list">
            {objectives.map(obj => {
              // Calculate objective progress: average of KR progress, excluding Deprioritized
              const includedKRs = (obj.keyResults || []).filter(kr => kr.status !== 'Deprioritized');
              const totalProgress = includedKRs.reduce((sum, kr) => {
                // Treat Achieved as 100%
                const progress = kr.status === 'Achieved' ? 100 : (kr.progress || 0);
                return sum + progress;
              }, 0);
              const avgProgress = includedKRs.length > 0 ? Math.round(totalProgress / includedKRs.length) : 0;
              const isEditing = editingObjectiveId === obj.id;
              const isExpanded = expandedObjectiveIds.has(obj.id) || isEditing;
              
              return (
                <div key={obj.id} className={`objective-subcard ${isEditing ? 'objective-editing' : ''}`}>
                  {/* Objective Header */}
                  <div className="objective-header">
                    {isEditing ? (
                      // Edit mode: editable name input
                      <input
                        type="text"
                        className="inline-edit-input objective-name-input"
                        value={editFormData?.name || ''}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Objective name"
                      />
                    ) : (
                      // View mode: clickable to expand
                      <h3 
                        onClick={() => {
                          const next = new Set(expandedObjectiveIds);
                          next.has(obj.id) ? next.delete(obj.id) : next.add(obj.id);
                          setExpandedObjectiveIds(next);
                        }} 
                        style={{ cursor: 'pointer', flex: 1 }}
                      >
                        {obj.name}
                      </h3>
                    )}
                    
                    <div className="objective-header-actions">
                      {!isEditing && (
                        <span className="objective-score-pill" title="Average of KR progress">
                          {includedKRs.length > 0 ? `${avgProgress}%` : '—'}
                        </span>
                      )}
                      
                      {isEditing ? (
                        // Edit mode: quarter dropdown
                        <select
                          className="inline-edit-select"
                          value={editFormData?.quarter || ''}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, quarter: e.target.value }))}
                        >
                          {getQuarterOptions().map(q => (
                            <option key={q} value={q}>{q.replace('-', ' ')}</option>
                          ))}
                        </select>
                      ) : (
                        // View mode: edit and delete buttons
                        <>
                          <button 
                            className="inline-icon-btn edit-btn"
                            onClick={(e) => { e.stopPropagation(); handleStartEdit(obj); }}
                            title="Edit objective"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button 
                            className="inline-icon-btn delete-btn"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setDeleteModal({ 
                                isOpen: true, 
                                type: 'objective', 
                                item: obj, 
                                krCount: (obj.keyResults || []).length 
                              }); 
                            }}
                            title="Delete objective"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Key Results (expanded or editing) */}
                  {isExpanded && (
                    <div className="objective-krs">
                      {isEditing ? (
                        // Edit mode: editable KR inputs
                        <>
                          <div className="kr-edit-header">
                            <span className="kr-edit-label">Key Results</span>
                          </div>
                          {editFormData?.keyResults?.map((kr, idx) => (
                            <div key={kr.id} className="kr-edit-row">
                              <input
                                type="text"
                                className="inline-edit-input kr-text-input"
                                value={kr.text}
                                onChange={(e) => {
                                  const newKRs = [...editFormData.keyResults];
                                  newKRs[idx] = { ...newKRs[idx], text: e.target.value };
                                  setEditFormData(prev => ({ ...prev, keyResults: newKRs }));
                                }}
                                placeholder="Key result description"
                              />
                              <input
                                type="text"
                                className="inline-edit-input kr-target-input"
                                value={kr.target}
                                onChange={(e) => {
                                  const newKRs = [...editFormData.keyResults];
                                  newKRs[idx] = { ...newKRs[idx], target: e.target.value };
                                  setEditFormData(prev => ({ ...prev, keyResults: newKRs }));
                                }}
                                placeholder="Target"
                              />
                              <button 
                                className="inline-icon-btn delete-btn kr-delete-btn"
                                onClick={() => {
                                  if (kr.isNew) {
                                    setEditFormData(prev => ({
                                      ...prev,
                                      keyResults: prev.keyResults.filter(k => k.id !== kr.id)
                                    }));
                                    return;
                                  }
                                  const originalKR = obj.keyResults?.find(k => k.id === kr.id);
                                  setDeleteModal({ 
                                    isOpen: true, 
                                    type: 'kr', 
                                    item: originalKR || kr, 
                                    krCount: 0 
                                  });
                                }}
                                title="Delete key result"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                </svg>
                              </button>
                            </div>
                          ))}

                          <div className="kr-edit-header" style={{ marginTop: 16 }}>
                            <span className="kr-edit-label">Add Key Result</span>
                          </div>
                          <div className="kr-edit-row" style={{ alignItems: 'center', gap: 12 }}>
                            <select
                              className="inline-edit-select"
                              value={newKRForm.type}
                              onChange={(e) => setNewKRForm(prev => ({
                                ...prev,
                                type: e.target.value,
                                polarity: e.target.value === 'metric' ? (prev.polarity || 'up') : prev.polarity
                              }))}
                            >
                              <option value="metric">Metric</option>
                              <option value="milestone">Milestone</option>
                            </select>
                            <input
                              type="text"
                              className="inline-edit-input kr-text-input"
                              value={newKRForm.text}
                              onChange={(e) => setNewKRForm(prev => ({ ...prev, text: e.target.value }))}
                              placeholder="Key result description"
                            />
                            {newKRForm.type === 'metric' && (
                              <>
                                <input
                                  type="text"
                                  className="inline-edit-input kr-target-input"
                                  value={newKRForm.target}
                                  onChange={(e) => setNewKRForm(prev => ({ ...prev, target: e.target.value }))}
                                  placeholder="Target"
                                />
                                <input
                                  type="text"
                                  className="inline-edit-input kr-unit-input"
                                  value={newKRForm.unit}
                                  onChange={(e) => setNewKRForm(prev => ({ ...prev, unit: e.target.value }))}
                                  placeholder="Unit"
                                />
                              </>
                            )}
                            <button
                              className="inline-edit-save"
                              type="button"
                              onClick={handleAddKRToEdit}
                            >
                              Add KR
                            </button>
                          </div>
                          
                          {/* Save/Cancel buttons */}
                          <div className="inline-edit-actions">
                            <button 
                              className="inline-edit-save"
                              onClick={() => handleSaveEdit(obj.id)}
                              disabled={isSavingEdit}
                            >
                              {isSavingEdit ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button 
                              className="inline-edit-cancel"
                              onClick={handleCancelEdit}
                              disabled={isSavingEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        // View mode: regular KRRow components
                        obj.keyResults?.map(kr => (
                          <KRRow
                            key={kr.id}
                            kr={kr}
                            testSlackId={testSlackId}
                            setToast={setToast}
                            refreshMyPlan={refreshMyPlan}
                            clearHomeCache={clearHomeCache}
                            onUpdate={(updatedKR) => {
                              // Update local state immediately (optimistic)
                              // Don't call refreshMyPlan here - it would fetch stale data before PATCH completes
                              setMyPlanData(prev => ({
                                ...prev,
                                objectives: prev.objectives.map(o =>
                                  o.id === obj.id
                                    ? {
                                        ...o,
                                        keyResults: o.keyResults.map(k =>
                                          k.id === updatedKR.id ? updatedKR : k
                                        )
                                      }
                                    : o
                                )
                              }));
                            }}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <h3>No objectives yet</h3>
            <p>You don't have any objectives for {currentQuarter || mockData.currentQuarter} yet.</p>
            <div className="keyboard-hint">
              <kbd>⌘</kbd> + <kbd>K</kbd> opens actions
            </div>
          </div>
        )}
      </div>

      {/* Section 3: Unlinked Tasks (enhanced) */}
      {unlinkedTasks.length > 0 && (
        <div className="section-card unlinked-tasks-section">
          <div className="unlinked-header">
            <h2>Unlinked Tasks</h2>
            <div className="unlinked-sub">Unlinked work reduces your Focus Rate. Link tasks to stay aligned.</div>
          </div>
            <div className="tasks-list">
            {unlinkedTasks.map(task => {
              // FIX: Use date string comparison instead of Date objects
              const userTimezone = 'America/Los_Angeles'; // TODO: Get from user profile
              const todayStr = getTodayInTimezone(userTimezone);
              const threeDaysOutStr = addDaysToDateString(todayStr, 3);
              
              // Use effective due date (respects snooze_until)
              const effectiveDueDate = getEffectiveDueDate(task, todayStr);
              const isSnoozed = task.snoozeUntil && task.snoozeUntil > todayStr;
              
              let badgeClass = 'later';
              if (task.status === 'Done') badgeClass = 'done';
              else if (effectiveDueDate && effectiveDueDate < todayStr) badgeClass = 'overdue';
              else if (effectiveDueDate && effectiveDueDate <= threeDaysOutStr) badgeClass = 'soon';
              return (
                <div key={task.id} className="task-item">
                  <span className="task-text">{task.text}</span>
                  {effectiveDueDate && (
                    <span className={`task-due-badge ${badgeClass}`}>
                      {isSnoozed && '🕑 '}
                      {effectiveDueDate}
                    </span>
                  )}
                    <button className="link-task-btn" onClick={() => {
                      const allKRs = (objectives||[]).flatMap(o => o.keyResults || []);
                      if (allKRs.length === 0) { 
                        if (setToast) {
                          setToast('No KRs available to link');
                          setTimeout(() => setToast(null), 3000);
                        }
                        return; 
                      }
                      setTaskToLink(task);
                      setLinkKRModalOpen(true);
                    }}>Link to KR</button>
                    <button
                      className="delete-task-btn"
                      aria-label="Delete task"
                      title="Delete task"
                      onClick={() => handleDeleteTask(task.id)}
                    >
                      ×
                    </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state when no objectives and no tasks */}
      {objectives.length === 0 && unlinkedTasks.length === 0 && (
        <div className="empty-state">
          <h3>No plan yet</h3>
          <p>Use the OKR Editor to create your goals.</p>
          <div className="keyboard-hint">
            <kbd>⌘</kbd> + <kbd>K</kbd> opens actions
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component: Linked Tasks Modal
function LinkedTasksModal({ kr, testSlackId, setToast, refreshMyPlan, clearHomeCache, onClose }) {
  const [filter, setFilter] = useState('all');
  const [isProcessing, setIsProcessing] = useState(new Set()); // Track tasks being processed
  
  const filterTasks = (tasks) => {
    if (!tasks || tasks.length === 0) return [];
    
    // FIX: Use date string comparison in user's timezone
    const userTimezone = 'America/Los_Angeles'; // TODO: Get from user profile
    const todayStr = getTodayInTimezone(userTimezone);
    const weekEndStr = addDaysToDateString(todayStr, 6);
    
    switch (filter) {
      case 'week':
        return tasks.filter(t => {
          const effectiveDate = getEffectiveDueDate(t, todayStr);
          if (!effectiveDate) return false;
          return effectiveDate >= todayStr && effectiveDate <= weekEndStr;
        });
      case 'overdue':
        return tasks.filter(t => {
          const effectiveDate = getEffectiveDueDate(t, todayStr);
          if (!effectiveDate) return false;
          return effectiveDate < todayStr;
        });
      case 'nodate':
        return tasks.filter(t => !getEffectiveDueDate(t, getTodayInTimezone(userTimezone)));
      default:
        return tasks;
    }
  };
  
  const filteredTasks = filterTasks(kr.tasks);
  
  // Shared handler for task mutations (Complete, Edit, Unlink)
  const handleTaskAction = async (taskId, action, updates = {}) => {
    if (isProcessing.has(taskId)) return; // Prevent double-clicks
    
    setIsProcessing(prev => new Set(prev).add(taskId));
    try {
      if (action === 'complete') {
        await updateCapture(taskId, { status: 'Done' }, testSlackId);
        if (setToast) {
          setToast('Task completed');
          setTimeout(() => setToast(null), 2000);
        }
      } else if (action === 'edit') {
        // For MVP: simple inline edit - just update the text if provided
        if (updates.text) {
          await updateCapture(taskId, { text: updates.text }, testSlackId);
          if (setToast) {
            setToast('Task updated');
            setTimeout(() => setToast(null), 2000);
          }
        }
      } else if (action === 'unlink') {
        // Unlink by passing empty string (backend will set linked KR to empty array)
        await linkCapture(taskId, '', testSlackId);
        if (setToast) {
          setToast('Task unlinked');
          setTimeout(() => setToast(null), 2000);
        }
      }
      
      // Clear home cache (affects drift) and refresh MyPlan data
      if (clearHomeCache) clearHomeCache();
      if (refreshMyPlan) refreshMyPlan(false);
    } catch (error) {
      console.error(`Failed to ${action} task:`, error);
      if (setToast) {
        setToast(`Failed to ${action} task: ${error.message}`);
        setTimeout(() => setToast(null), 4000);
      }
    } finally {
      setIsProcessing(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Tasks Linked to KR</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-subheader">
          <span className="kr-title-modal">{kr.text}</span>
        </div>
        
        {/* Filter tabs */}
        <div className="modal-filters">
          <button 
            className={filter === 'all' ? 'filter-tab active' : 'filter-tab'}
            onClick={() => setFilter('all')}
          >
            All ({kr.tasks?.length || 0})
          </button>
          <button 
            className={filter === 'week' ? 'filter-tab active' : 'filter-tab'}
            onClick={() => setFilter('week')}
          >
            Due this week
          </button>
          <button 
            className={filter === 'overdue' ? 'filter-tab active' : 'filter-tab'}
            onClick={() => setFilter('overdue')}
          >
            Overdue
          </button>
          <button 
            className={filter === 'nodate' ? 'filter-tab active' : 'filter-tab'}
            onClick={() => setFilter('nodate')}
          >
            No date
          </button>
        </div>
        
        {/* Task list */}
        <div className="modal-task-list">
          {filteredTasks.length === 0 ? (
            <div className="empty-state-modal">
              <p>No tasks match this filter.</p>
            </div>
          ) : (
            filteredTasks.map(task => (
              <div key={task.id} className="modal-task-row">
                <div className="modal-task-main">
                  <span className="modal-task-title">{task.text}</span>
                  {(() => {
                    const userTimezone = 'America/Los_Angeles'; // TODO: Get from user profile
                    const todayStr = getTodayInTimezone(userTimezone);
                    const effectiveDate = getEffectiveDueDate(task, todayStr);
                    const isSnoozed = task.snoozeUntil && task.snoozeUntil > todayStr;
                    return effectiveDate ? (
                      <span className="modal-task-due">
                        {isSnoozed && '🕑 '}
                        {effectiveDate}
                      </span>
                    ) : null;
                  })()}
                  <span className={`modal-task-status ${task.status === 'Done' ? 'done' : 'open'}`}>
                    {task.status || 'Open'}
                  </span>
                </div>
                <div className="modal-task-actions">
                  {task.status !== 'Done' && (
                    <button 
                      className="modal-btn-sm"
                      onClick={() => handleTaskAction(task.id, 'complete')}
                      disabled={isProcessing.has(task.id)}
                    >
                      {isProcessing.has(task.id) ? '...' : 'Complete'}
                    </button>
                  )}
                  <button 
                    className="modal-btn-sm"
                    onClick={() => {
                      // Simple inline edit: prompt for new text
                      const newText = prompt('Edit task:', task.text);
                      if (newText && newText.trim() && newText !== task.text) {
                        handleTaskAction(task.id, 'edit', { text: newText.trim() });
                      }
                    }}
                    disabled={isProcessing.has(task.id)}
                  >
                    Edit
                  </button>
                  <button 
                    className="modal-btn-sm danger"
                    onClick={() => {
                      if (window.confirm('Unlink this task from this KR?')) {
                        handleTaskAction(task.id, 'unlink');
                      }
                    }}
                    disabled={isProcessing.has(task.id)}
                  >
                    Unlink
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Helper component: Key Result Row (Stacked Layout with Progress)
function KRRow({ kr, onUpdate, testSlackId, setToast, refreshMyPlan, clearHomeCache }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [error, setError] = useState(null);
  const [blockedNoteError, setBlockedNoteError] = useState(false);
  const updateTimeoutRef = useRef(null);
  
  // Determine if this is a metric or milestone
  const isMetric = kr.type?.toLowerCase() === 'metric';
  const isMilestone = !isMetric;
  
  // Milestone-specific state
  const milestoneCompleted = kr.milestoneCompleted || false;
  const milestoneConfidence = kr.milestoneConfidence || 'Confident';
  const milestoneStage = kr.milestoneStage || 'Not started';
  
  // Auto-show notes for Blocked milestones
  useEffect(() => {
    if (isMilestone && milestoneConfidence === 'Blocked' && !milestoneCompleted) {
      setShowNote(true);
    }
  }, [isMilestone, milestoneConfidence, milestoneCompleted]);
  
  // Debounced update function
  const debouncedUpdate = useCallback((updates) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(async () => {
      setIsUpdating(true);
      setError(null);
      
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (ENV_CONFIG.DEV_MODE && testSlackId) {
          headers['X-Slack-Id'] = testSlackId;
        }
        
        const response = await fetch(`${ENV_CONFIG.API_URL}/api/kr/${kr.id}`, {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify(updates)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }
        
        const data = await response.json();
        onUpdate({ 
          ...kr, 
          ...data.kr,
          updatedAt: data.kr.updatedAt || new Date().toISOString()
        });
      } catch (error) {
        console.error('Failed to update KR:', error);
        setError(error.message);
        onUpdate(kr);
      } finally {
        setIsUpdating(false);
      }
    }, 500);
  }, [kr, onUpdate, testSlackId]);
  
  // Handle actual value change (for metrics only)
  const handleActualChange = (value) => {
    const actual = value === '' ? null : value;
    onUpdate({ ...kr, actual });
    debouncedUpdate({ actual });
  };
  
  // Handle status change (for metrics only now)
  const handleStatusChange = (newStatus) => {
    onUpdate({ ...kr, status: newStatus });
    debouncedUpdate({ status: newStatus });
  };
  
  // Handle milestone confidence change
  const handleConfidenceChange = (newConfidence) => {
    // Validate: Blocked requires note
    if (newConfidence === 'Blocked' && !kr.note?.trim()) {
      setBlockedNoteError(true);
      setShowNote(true);
      // Still update locally but show error
    } else {
      setBlockedNoteError(false);
    }
    onUpdate({ ...kr, milestoneConfidence: newConfidence });
    debouncedUpdate({ milestoneConfidence: newConfidence });
  };
  
  // Handle milestone stage change
  const handleStageChange = (newStage) => {
    onUpdate({ ...kr, milestoneStage: newStage });
    debouncedUpdate({ milestoneStage: newStage });
  };
  
  // Handle Mark Complete
  const handleMarkComplete = () => {
    // Validate: Blocked requires note
    if (milestoneConfidence === 'Blocked' && !kr.note?.trim()) {
      setBlockedNoteError(true);
      setShowNote(true);
      setToast('Add a note to explain why this is blocked');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    
    // Note: milestoneCompletedAt is auto-computed by Airtable
    onUpdate({ 
      ...kr, 
      milestoneCompleted: true, 
      milestoneStage: 'Done'
    });
    debouncedUpdate({ 
      milestoneCompleted: true, 
      milestoneStage: 'Done'
    });
    setToast('Milestone completed');
    setTimeout(() => setToast(null), 2000);
  };
  
  // Handle Undo Complete
  const handleUndoComplete = () => {
    // Note: milestoneCompletedAt is auto-computed by Airtable
    onUpdate({ 
      ...kr, 
      milestoneCompleted: false, 
      milestoneStage: 'In progress'
    });
    debouncedUpdate({ 
      milestoneCompleted: false, 
      milestoneStage: 'In progress'
    });
    setToast('Completion undone');
    setTimeout(() => setToast(null), 2000);
  };
  
  // Handle note change
  const handleNoteChange = (newNote) => {
    onUpdate({ ...kr, note: newNote });
    debouncedUpdate({ note: newNote });
    // Clear blocked error if note is now provided
    if (newNote?.trim() && milestoneConfidence === 'Blocked') {
      setBlockedNoteError(false);
    }
  };
  
  // Relative time helper
  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Never updated';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };
  
  // Format date for completion timestamp
  const formatCompletedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Format number with commas
  const formatNumber = (num) => {
    if (!num) return num;
    const numValue = parseFloat(num);
    if (isNaN(numValue)) return num;
    return numValue.toLocaleString('en-US');
  };
  
  // Status color mapping (for metrics)
  const statusColors = {
    'On Track': 'var(--success-green, #22c55e)',
    'At Risk': 'var(--warning-amber, #f59e0b)',
    'Off Track': 'var(--error-red, #ef4444)',
    'Deprioritized': 'var(--neutral-gray, #9ca3af)',
    'Achieved': 'var(--info-blue, #3b82f6)'
  };
  
  // Confidence color mapping (for milestones)
  const confidenceColors = {
    'Confident': 'var(--success-green, #22c55e)',
    'Uncertain': 'var(--warning-amber, #f59e0b)',
    'Blocked': 'var(--error-red, #ef4444)'
  };
  
  // Check if metric is configured
  const isConfigured = isMetric ? (kr.target && kr.unit) : true;
  
  let configError = '';
  if (isMetric && !isConfigured) {
    if (!kr.target && !kr.unit) {
      configError = 'Set target and unit to track progress';
    } else if (!kr.target) {
      configError = 'Set a target to track progress';
    } else if (!kr.unit) {
      configError = 'Set a unit (e.g., days, sessions)';
    }
  }
  
  return (
    <div className={`kr-row-stacked ${isMilestone ? 'kr-milestone' : 'kr-metric'} ${milestoneCompleted ? 'kr-completed' : ''}`}>
      {/* Line 1: Title and controls */}
      <div className="kr-line-1">
        <div className="kr-title-with-target">
          <span className="kr-title-bold">{kr.text}</span>
          {isMetric && kr.target && (
            <span className="kr-target-inline">
              Target: {formatNumber(kr.target)}{kr.unit ? ` ${kr.unit}` : ''}
            </span>
          )}
        </div>
        
        <div className="kr-controls">
          {/* METRIC CONTROLS */}
          {isMetric && (
            <>
              {!isConfigured ? (
                <div className="kr-unconfigured">
                  <span className="kr-config-error">{configError}</span>
                  <span className="kr-config-badge">Not configured</span>
                </div>
              ) : (
                <>
                  <input
                    type="number"
                    className="kr-actual-input"
                    value={kr.actual || ''}
                    onChange={(e) => handleActualChange(e.target.value)}
                    placeholder="0"
                    disabled={isUpdating}
                  />
                  <span className="kr-unit-label">{kr.unit || ''}</span>
                  <span className="kr-progress-badge">{kr.progress || 0}%</span>
                  <select
                    className="kr-status-dropdown"
                    value={kr.status || 'On Track'}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={isUpdating}
                    style={{ color: statusColors[kr.status || 'On Track'] }}
                  >
                    <option value="On Track">On Track</option>
                    <option value="At Risk">At Risk</option>
                    <option value="Off Track">Off Track</option>
                    <option value="Deprioritized">Deprioritized</option>
                    <option value="Achieved">Achieved</option>
                  </select>
                </>
              )}
            </>
          )}
          
          {/* MILESTONE CONTROLS */}
          {isMilestone && (
            <>
              {milestoneCompleted ? (
                // Completed state: locked, show undo
                <div className="milestone-completed-state">
                  <span className="milestone-completed-badge">
                    Completed
                  </span>
                  <button 
                    className="milestone-undo-btn"
                    onClick={handleUndoComplete}
                    disabled={isUpdating}
                    title="Mark as incomplete"
                  >
                    undo
                  </button>
                </div>
              ) : (
                // Active state: show confidence, stage, mark complete
                <div className="milestone-controls-group">
                  <div className="milestone-field">
                    <span className="milestone-field-label">Confidence</span>
                    <select
                      className="milestone-confidence-select"
                      value={milestoneConfidence}
                      onChange={(e) => handleConfidenceChange(e.target.value)}
                      disabled={isUpdating}
                      style={{ color: confidenceColors[milestoneConfidence] }}
                    >
                      <option value="Confident">Confident</option>
                      <option value="Uncertain">Uncertain</option>
                      <option value="Blocked">Blocked</option>
                    </select>
                  </div>
                  <div className="milestone-field">
                    <span className="milestone-field-label">Progress</span>
                    <select
                      className="milestone-stage-select"
                      value={milestoneStage}
                      onChange={(e) => handleStageChange(e.target.value)}
                      disabled={isUpdating}
                    >
                      <option value="Not started">Not started</option>
                      <option value="In progress">In progress</option>
                      <option value="Nearly there">Nearly there</option>
                      <option value="Done">Done</option>
                    </select>
                  </div>
                  <button 
                    className="milestone-complete-btn"
                    onClick={handleMarkComplete}
                    disabled={isUpdating}
                  >
                    Mark Complete
                  </button>
                </div>
              )}
            </>
          )}
          
          {/* Note toggle - show for both types, but required indicator for blocked milestones */}
          {(!isMilestone || !milestoneCompleted) && (
            <button 
              className={`kr-note-toggle ${blockedNoteError ? 'note-required' : ''}`}
              onClick={() => setShowNote(!showNote)}
              title={kr.note ? `Note: ${kr.note.slice(0, 50)}${kr.note.length > 50 ? '...' : ''}` : "Add note"}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M7 4.5V4.51M7 6.5V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* Line 2: Last updated or Completed timestamp */}
      <div className="kr-line-2">
        {isMilestone && milestoneCompleted && kr.milestoneCompletedAt ? (
          <span className="kr-completed-time">
            Completed {formatCompletedDate(kr.milestoneCompletedAt)}
          </span>
        ) : (
          <span className="kr-updated-time">
            Last updated: {getRelativeTime(kr.updatedAt)}
          </span>
        )}
      </div>
      
      {/* Line 3: Tasks linked (clickable) */}
      {kr.tasks && kr.tasks.length > 0 && (
        <div className="kr-line-3">
          <button 
            className="kr-tasks-link"
            onClick={() => setShowTasksModal(true)}
          >
            {kr.tasks.length} task{kr.tasks.length !== 1 ? 's' : ''} linked
            <span className="chevron">›</span>
          </button>
        </div>
      )}
      
      {/* Tasks Modal */}
      {showTasksModal && (
        <LinkedTasksModal 
          kr={kr} 
          testSlackId={testSlackId}
          setToast={setToast}
          refreshMyPlan={refreshMyPlan}
          clearHomeCache={clearHomeCache}
          onClose={() => setShowTasksModal(false)}
        />
      )}
      
      {/* Note textarea - disabled when milestone is completed */}
      {showNote && (
        <div className={`kr-note-area ${blockedNoteError ? 'note-error' : ''}`}>
          <textarea
            className="kr-note-textarea"
            value={kr.note || ''}
            onChange={(e) => handleNoteChange(e.target.value)}
            placeholder={milestoneConfidence === 'Blocked' ? "Add a short note explaining the blocker..." : "Add a note (max 500 characters)..."}
            maxLength={500}
            disabled={isUpdating || (isMilestone && milestoneCompleted)}
          />
          <div className="kr-note-footer">
            {blockedNoteError && (
              <span className="kr-note-error-msg">Required when Blocked</span>
            )}
            <span className="kr-note-count">{(kr.note || '').length}/500</span>
          </div>
        </div>
      )}
      
      {/* Error toast */}
      {error && (
        <div className="kr-error-toast">
          Failed to save: {error}
        </div>
      )}
    </div>
  );
}

// Add Task Modal Component
function AddTaskModal({ isOpen, onClose, onSubmit, setToast }) {
  const [taskTitle, setTaskTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!taskTitle.trim() || isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      await onSubmit(taskTitle); // Wait for API to complete
      setTaskTitle('');
      onClose(); // Only close on success
    } catch (error) {
      console.error('Failed to add task:', error);
      if (setToast) {
        setToast(`Failed to add task: ${error.message || 'Please try again.'}`);
      }
      // Keep modal open so user can retry
    } finally {
      setIsSubmitting(false); // Always re-enable
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-task-modal-overlay" onClick={onClose}>
      <div className="add-task-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-task-modal-header">
          <h3>Add New Task</h3>
          <button className="add-task-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="add-task-modal-body">
          <label className="add-task-label">What needs to get done?</label>
          <input
            type="text"
            className="add-task-input"
            placeholder="Send plan to @alex by Friday"
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <div className="add-task-format-hint">
            You can @mention teammates and add deadlines like "by Friday" or "by end of month"
          </div>
          
          <div className="add-task-divider"></div>
          
          <div className="add-task-slack-note">
            <svg width="20" height="20" viewBox="0 0 54 54" fill="none">
              <path d="M19.712 11.428a4.285 4.285 0 01-8.57 0 4.285 4.285 0 018.57 0zm14.857 0a4.285 4.285 0 104.286 4.286h-4.286V11.428z" fill="#E01E5A"/>
              <path d="M38.855 15.714a4.286 4.286 0 118.571 0v10.714a4.286 4.286 0 11-8.571 0V15.714z" fill="#36C5F0"/>
              <path d="M43.141 38.855a4.286 4.286 0 110 8.571 4.286 4.286 0 010-8.571zm0-14.856a4.286 4.286 0 10-4.286-4.286v4.286h4.286z" fill="#2EB67D"/>
              <path d="M38.855 19.713a4.286 4.286 0 11-8.571 0V8.999a4.286 4.286 0 118.571 0v10.714z" fill="#ECB22E"/>
              <path d="M15.427 19.713a4.286 4.286 0 100-8.571 4.286 4.286 0 000 8.571z" fill="#E01E5A"/>
              <path d="M15.427 24a4.286 4.286 0 104.286 4.286V24h-4.286z" fill="#36C5F0"/>
              <path d="M19.713 38.855a4.286 4.286 0 108.571 0 4.286 4.286 0 00-8.571 0z" fill="#2EB67D"/>
              <path d="M34.57 34.57a4.286 4.286 0 100 8.57H45.284a4.286 4.286 0 100-8.57H34.569z" fill="#ECB22E"/>
            </svg>
            <div className="add-task-slack-text">
              <strong>Prefer Slack?</strong> Type <code>/capture</code> in any channel
            </div>
          </div>
        </div>
        
        <div className="add-task-modal-footer">
          <button className="add-task-cancel-btn" onClick={onClose}>Cancel</button>
          <button 
            className="add-task-submit-btn" 
            onClick={handleSubmit}
            disabled={!taskTitle.trim() || isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Link to KR Modal Component
function LinkToKRModal({ isOpen, onClose, keyResults, onLink, taskName }) {
  const [selectedKRId, setSelectedKRId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredKRs = keyResults.filter(kr => 
    kr.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = () => {
    if (selectedKRId) {
      const selectedKR = keyResults.find(kr => kr.id === selectedKRId);
      onLink(selectedKR);
      setSelectedKRId(null);
      setSearchTerm('');
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && selectedKRId) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-task-modal-overlay" onClick={onClose}>
      <div className="link-kr-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-task-modal-header">
          <div>
            <h3>Link Task to Key Result</h3>
            {taskName && <div style={{fontSize: '14px', color: '#64748B', marginTop: '4px'}}>"{taskName}"</div>}
          </div>
          <button className="add-task-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="add-task-modal-body" style={{maxHeight: '400px', overflow: 'auto'}}>
          <input
            type="text"
            className="add-task-input"
            placeholder="Search key results..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            style={{marginBottom: '16px'}}
          />
          
          <div className="kr-list-select">
            {filteredKRs.length === 0 ? (
              <div style={{textAlign: 'center', padding: '24px', color: '#94A3B8'}}>
                {searchTerm ? 'No key results match your search' : 'No key results available'}
              </div>
            ) : (
              filteredKRs.map(kr => (
                <label key={kr.id} className="kr-select-item">
                  <input
                    type="radio"
                    name="kr-select"
                    value={kr.id}
                    checked={selectedKRId === kr.id}
                    onChange={() => setSelectedKRId(kr.id)}
                  />
                  <div className="kr-select-content">
                    <div className="kr-select-title">{kr.text}</div>
                    <div className="kr-select-meta">
                      {kr.progress !== undefined && <span className="kr-progress-badge">{kr.progress}%</span>}
                      {kr.type && <span className="kr-type-badge-small">{kr.type === 'metric' ? '📊 Metric' : '🎯 Milestone'}</span>}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
        
        <div className="add-task-modal-footer">
          <button className="add-task-cancel-btn" onClick={onClose}>Cancel</button>
          <button 
            className="add-task-submit-btn" 
            onClick={handleSubmit}
            disabled={!selectedKRId}
          >
            Link Task
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper component: This Week's Focus
// Date Picker Modal Component
function DatePickerModal({ isOpen, onClose, task, onSave }) {
  const [selectedDate, setSelectedDate] = useState('');

  // Initialize date when task changes
  useEffect(() => {
    if (task) {
      setSelectedDate(task.dueDate || '');
    }
  }, [task]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(selectedDate || null);
  };

  const handleClearDate = () => {
    setSelectedDate('');
  };

  return (
    <div className="add-task-modal-overlay" onClick={onClose}>
      <div className="add-task-modal-content date-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-task-modal-header">
          <h3>Edit Due Date</h3>
          <button className="add-task-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="add-task-modal-body">
            {task && (
              <div style={{marginBottom: '16px', padding: '12px', background: '#F8FAFC', borderRadius: '8px'}}>
                <div style={{fontSize: '14px', color: '#64748B', marginBottom: '4px'}}>Task</div>
                <div style={{fontSize: '15px', fontWeight: 500, color: '#0F172A'}}>{task.text}</div>
              </div>
            )}
            
            <label className="add-task-label">Due Date</label>
            <input
              type="date"
              className="add-task-input date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              autoFocus
            />
            
            {selectedDate && (
              <button 
                type="button"
                className="clear-date-btn" 
                onClick={handleClearDate}
              >
                Clear Date
              </button>
            )}
          </div>

          <div className="add-task-modal-footer">
            <button type="button" className="add-task-cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="add-task-submit-btn">
              Save Date
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ThisWeeksFocus({ captures, onAddTask, onMarkComplete, onDeleteTask, onLinkToKR, onEditDate, setToast }) {
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [laterCollapsed, setLaterCollapsed] = useState(true);
  const [noDateCollapsed, setNoDateCollapsed] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  
  // Use captures directly - all open captures for the user
  const tasks = captures || [];

  // FIX: Use date strings in user's timezone (same as backend) instead of browser Date objects
  // This prevents timezone mismatches where "tomorrow" in PST shows as "today" in EST browser
  const userTimezone = 'America/Los_Angeles'; // TODO: Get from user profile when available
  const todayStr = getTodayInTimezone(userTimezone); // "YYYY-MM-DD" in user's timezone
  const weekEndStr = addDaysToDateString(todayStr, 6); // Sunday (6 days from today)
  
  const groups = { overdue: [], today: [], week: [], later: [], noDate: [] };
  
  tasks.forEach(t => {
    // Use effective due date (respects snooze_until if set and future)
    const effectiveDueDate = getEffectiveDueDate(t, todayStr);
    
    if (!effectiveDueDate) {
      groups.noDate.push(t);
      return;
    }
    
    // Compare date strings directly (lexicographic comparison works for YYYY-MM-DD)
    const isOverdue = effectiveDueDate < todayStr;
    const isToday = effectiveDueDate === todayStr;
    const isThisWeek = effectiveDueDate > todayStr && effectiveDueDate <= weekEndStr;
    const isLater = effectiveDueDate > weekEndStr;
    
    // Overdue tasks go in overdue section
    if (isOverdue) {
      groups.overdue.push(t);
      // Continue to also bucket by original date (per spec: keep in original buckets)
    }
    
    // Bucket by date (overdue tasks appear in both overdue AND their original bucket)
    if (isToday) {
      groups.today.push(t);
    } else if (isThisWeek) {
      groups.week.push(t);
    } else if (isLater) {
      groups.later.push(t);
    }
  });

  const dueThisWeek = groups.today.length + groups.week.length;

  const Section = ({label, items, collapsible, collapsed, onToggle, isOverdue}) => (
    <div style={{marginBottom: 24}}>
      <div 
        className="task-section-label"
        style={{
          ...(collapsible ? {cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6} : {display: 'flex', alignItems: 'center', gap: 6}),
          ...(isOverdue ? {color: '#DC2626'} : {})
        }}
        onClick={collapsible ? onToggle : undefined}
      >
        {collapsible && (
          <svg width="12" height="12" viewBox="0 0 12 12" style={{transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}}>
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        )}
        {isOverdue && <span style={{fontSize: 14}}>⚠️</span>}
        {label}
        {!collapsible && items.length > 0 && <span style={{fontSize: 12, opacity: 0.6}}>({items.length})</span>}
        {collapsible && <span style={{fontSize: 12, opacity: 0.6}}>({items.length})</span>}
      </div>
      {(!collapsible || !collapsed) && (
        items.length === 0 ? (
        <div className="task-empty-state">Nothing here yet.</div>
      ) : (
        items.map(t => (
            <div 
              key={t.id} 
              className={`task-card-zen ${t.krTitle ? 'task-card-linked' : ''}`}
            >
            <div className="task-card-main">
              <div className="task-card-content">
                <div className="task-card-title">{t.text}</div>
                <div className="task-card-meta">
                  {t.krTitle && (
                      <span className="task-kr-badge">{t.krTitle}</span>
                  )}
                  {(() => {
                    const effectiveDate = getEffectiveDueDate(t, todayStr);
                    const isSnoozed = t.snoozeUntil && t.snoozeUntil > todayStr;
                    return effectiveDate ? (
                      <span className="task-due-badge">
                        {isSnoozed && '🕑 '}
                        {effectiveDate}
                      </span>
                    ) : null;
                  })()}
                </div>
              </div>
              <div className="task-card-actions">
                <button 
                  className="task-icon-btn task-complete-btn" 
                  onClick={()=>onMarkComplete(t.id)}
                  data-tooltip="Mark complete"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13 4L6 11L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button 
                  className="task-icon-btn task-edit-date-btn" 
                  onClick={()=>setEditingTask(t)}
                  data-tooltip="Edit date"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12.5 2.5H3.5C2.94772 2.5 2.5 2.94772 2.5 3.5V12.5C2.5 13.0523 2.94772 13.5 3.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5V3.5C13.5 2.94772 13.0523 2.5 12.5 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11 1V4M5 1V4M2.5 6.5H13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                  {!t.krTitle && (
                <button 
                  className="task-icon-btn task-link-btn" 
                      onClick={()=>onLinkToKR(t.id)}
                  data-tooltip="Link to Key Result"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M7 9L9 7M10.5 3.5L11.5 2.5C12.3284 1.67157 13.6716 1.67157 14.5 2.5C15.3284 3.32843 15.3284 4.67157 14.5 5.5L13.5 6.5M2.5 13.5L1.5 14.5C0.671573 15.3284 0.671573 16.6716 1.5 17.5C2.32843 18.3284 3.67157 18.3284 4.5 17.5L5.5 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
                  )}
                <button 
                  className="task-icon-btn task-delete-btn" 
                  onClick={()=>onDeleteTask(t.id)}
                  data-tooltip="Delete task"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M5 3V2C5 1.44772 5.44772 1 6 1H10C10.5523 1 11 1.44772 11 2V3M2 3H14M12.5 3L12 13C12 13.5523 11.5523 14 11 14H5C4.44772 14 4 13.5523 4 13L3.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))
        )
      )}
    </div>
  );

  return (
    <>
      <AddTaskModal 
        isOpen={isAddTaskModalOpen} 
        onClose={() => setIsAddTaskModalOpen(false)}
        onSubmit={(title) => onAddTask(title)}
        setToast={setToast}
      />
      
      <DatePickerModal
        isOpen={!!editingTask}
        onClose={() => setEditingTask(null)}
        task={editingTask}
        onSave={(newDate) => {
          if (onEditDate && editingTask) {
            onEditDate(editingTask.id, newDate);
            setEditingTask(null);
          }
        }}
      />
      
      <div className="section-card this-week-section">
        <div className="section-header">
          <div style={{fontSize:16, fontWeight:600, color:'#0F172A'}}>This Week's Focus</div>
          <button className="primary-btn" onClick={() => setIsAddTaskModalOpen(true)}>
            + Add Task
          </button>
        </div>
        {groups.overdue.length > 0 && (
          <Section label="Overdue" items={groups.overdue} isOverdue={true} />
        )}
        <Section label="Due Today" items={groups.today} />
        <Section label="Due This Week" items={groups.week} />
        <Section 
          label="Later" 
          items={groups.later} 
          collapsible={true} 
          collapsed={laterCollapsed} 
          onToggle={() => setLaterCollapsed(!laterCollapsed)}
        />
        <Section 
          label="No Date" 
          items={groups.noDate} 
          collapsible={true} 
          collapsed={noDateCollapsed} 
          onToggle={() => setNoDateCollapsed(!noDateCollapsed)}
        />
        <div className="microtext">{dueThisWeek} tasks due this week · {groups.overdue.length} overdue · {groups.noDate.length} with no date</div>
      </div>
    </>
  );
}

// OKR Editor Component
function OKREditor({ testSlackId, setToast, setFeedbackModal, currentQuarter }) {
  const [mode, setMode] = useState(null); // 'teach', 'paste', or 'edit'
  const [okrs, setOkrs] = useState([]);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [parsedData, setParsedData] = useState(null); // For reviewing parsed OKRs before save
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter || getCurrentQuarter()); // Track quarter for this creation session

  if (!mode) {
    return (
      <div className="okr-editor-page">
        <div className="okr-editor-content-block">
          {/* Header Section */}
          <div className="okr-editor-header">
            <h1 className="okr-editor-title">OKR Editor</h1>
            <p className="okr-editor-subtitle">Choose how you'd like to create your goals</p>
          </div>
          
          {/* Mode Selection */}
          <div className="mode-selection">
            <button 
              className="mode-btn teach-mode"
              onClick={() => setMode('teach')}
            >
              <div className="mode-icon-wrapper teach-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
              </div>
              <span className="mode-title">Teach Mode</span>
              <span className="mode-desc">Step-by-step wizard to build OKRs</span>
            </button>
            
            <button 
              className="mode-btn quick-mode"
              onClick={() => setMode('paste')}
            >
              <div className="mode-icon-wrapper paste-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  <path d="M9 14l2 2 4-4"/>
                </svg>
              </div>
              <span className="mode-title">Quick Mode</span>
              <span className="mode-desc">Type or paste in any format</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'teach') {
    return <TeachModeWizard onComplete={setOkrs} onReview={() => setIsReviewMode(true)} testSlackId={testSlackId} setToast={setToast} setFeedbackModal={setFeedbackModal} currentQuarter={currentQuarter} />;
  }

  if (mode === 'paste' && !parsedData) {
    return <QuickMode 
      onParsed={(data) => {
        setParsedData(data);
      }} 
      testSlackId={testSlackId} 
      setToast={setToast}
      setFeedbackModal={setFeedbackModal}
      currentQuarter={selectedQuarter}
      setCurrentQuarter={setSelectedQuarter}
    />;
  }

  if (parsedData) {
    return <ReviewParsedOKRs 
      parsedData={parsedData}
      onSave={(savedOkrs) => {
        setOkrs(savedOkrs);
        setParsedData(null);
        setIsReviewMode(true);
      }}
      onReparse={() => {
        setParsedData(null);
      }}
      testSlackId={testSlackId}
      setToast={setToast}
      setFeedbackModal={setFeedbackModal}
      selectedQuarter={selectedQuarter}
      setSelectedQuarter={setSelectedQuarter}
    />;
  }

  if (isReviewMode) {
    return <ReviewScreen okrs={okrs} onBack={() => setIsReviewMode(false)} />;
  }

  return null;
}

// Teach Mode Wizard
function TeachModeWizard({ onComplete, onReview, testSlackId, setToast, setFeedbackModal, currentQuarter }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [objective, setObjective] = useState('');
  const [keyResults, setKeyResults] = useState([]);
  const [currentKR, setCurrentKR] = useState({ polarity: 'up' }); // Default to "Higher is better"
  const [showHelp, setShowHelp] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSavingOKRs, setIsSavingOKRs] = useState(false);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShowHelp(!showHelp);
      }
      if (e.key === 'Escape') {
        setShowHelp(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showHelp]);

  const addKR = () => {
    if (currentKR.title && (currentKR.type === 'metric' ? currentKR.target : currentKR.dueDate)) {
      setKeyResults([...keyResults, { ...currentKR, id: Date.now() }]);
      setCurrentKR({ polarity: 'up' }); // Reset with default polarity
      setShowAdvanced(false); // Reset advanced options
    }
  };

  const completeWizard = async () => {
    if (isSavingOKRs) return; // Prevent double-click
    setIsSavingOKRs(true);
    
    try {
      const okrData = {
        objective,
        keyResults,
        owner: mockSession.userId,
        quarter: mockData.currentQuarter
      };
      
      // Save to database via API
      // In dev mode, use testSlackId; in prod, pass null to use session cookie
      const slackId = ENV_CONFIG.DEV_MODE ? testSlackId : null;
      const result = await saveOKRs(okrData, mockData.currentQuarter, slackId);
      
      // Show prominent success modal for major milestone
      if (setFeedbackModal) {
        setFeedbackModal({
          type: 'success',
          title: 'Your OKRs are live',
          message: "They're now visible in My Plan.",
          action: {
            label: 'View My Plan',
            onClick: () => navigate('/plan')
          },
          dismissLabel: 'Stay here'
        });
      }
      
      // Create OKR object for the review screen
    const okrs = [{
      id: Date.now(),
      objective,
      keyResults,
      owner: mockSession.userId,
        quarter: mockData.currentQuarter,
        status: 'created', // Mark as created
        objectiveId: result.objective?.id,
        krIds: result.krs?.map(kr => kr.id) || []
    }];
      
    onComplete(okrs);
    onReview();
    } catch (error) {
      console.error('Failed to save OKRs:', error);
      // Show prominent error modal for blocking failure
      if (setFeedbackModal) {
        setFeedbackModal({
          type: 'error',
          title: 'Could not save OKRs',
          message: error.message || 'Try again. Contact support if this continues.',
          dismissLabel: 'Close'
        });
      }
    } finally {
      setIsSavingOKRs(false); // Always re-enable button
    }
  };

  const goBackToModeSelection = () => {
    // Navigate back to OKR Editor page (mode selection)
    window.history.back();
  };

  return (
    <div className="page">
      <div className="wizard-header">
        <h1>Teach Mode: Build OKRs Step by Step</h1>
        <p className="quarter-indicator">Creating OKRs for {currentQuarter || mockData.currentQuarter}</p>
        <div className="wizard-progress">
          <span className={`step ${step >= 1 ? 'active' : ''}`}>1. Objective</span>
          <span className={`step ${step >= 2 ? 'active' : ''}`}>2. Key Results</span>
          <span className={`step ${step >= 3 ? 'active' : ''}`}>3. Review</span>
        </div>
        <div className="keyboard-hint">
          
        </div>
      </div>

      {showHelp && (
        <div className="help-panel">
          <h3>Keyboard Shortcuts</h3>
          <ul>
            <li><strong>Tab</strong> - Navigate between fields</li>
            <li><strong>Enter</strong> - Add Key Result (when form is complete)</li>
            <li><strong>⌘/</strong> - Toggle this help panel</li>
            <li><strong>Escape</strong> - Close help panel</li>
          </ul>
          <button onClick={() => setShowHelp(false)} className="close-help-btn">Close</button>
        </div>
      )}

      {step === 1 && (
        <div className="wizard-step">
          <h2>What's your Objective?</h2>
          <textarea
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            placeholder="Describe what you want to achieve..."
            rows={4}
            className="objective-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                setStep(2);
              }
            }}
          />
          <div className="wizard-actions">
            <button onClick={goBackToModeSelection} className="wizard-btn secondary">
              Back
            </button>
          <button 
            onClick={() => setStep(2)}
            disabled={!objective.trim()}
            className="wizard-btn"
          >
            Next: Add Key Results
          </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="wizard-step">
          {/* Display the objective */}
          <div className="wizard-objective-display">
            <div className="wizard-objective-label">Your Objective</div>
            <div className="wizard-objective-text">{objective}</div>
            <button 
              onClick={() => setStep(1)} 
              className="wizard-objective-edit-btn"
              title="Edit objective"
            >
              Edit
            </button>
          </div>

          <h2>Add Key Results (1-3 recommended)</h2>
          
          <div className="kr-form">
            <div className="kr-type-selector">
              <label>
                <input
                  type="radio"
                  name="krType"
                  value="metric"
                  checked={currentKR.type === 'metric'}
                  onChange={(e) => {
                    setCurrentKR({ ...currentKR, type: e.target.value, polarity: 'up' });
                    setShowAdvanced(false);
                  }}
                />
                Metric (measurable outcome)
              </label>
              <label>
                <input
                  type="radio"
                  name="krType"
                  value="milestone"
                  checked={currentKR.type === 'milestone'}
                  onChange={(e) => {
                    setCurrentKR({ ...currentKR, type: e.target.value });
                    setShowAdvanced(false);
                  }}
                />
                Milestone (deliverable)
              </label>
            </div>

            {currentKR.type && (
              <div className="kr-fields">
                <div className="kr-title-wrapper">
                  <div className="kr-label-row">
                    <div className="kr-field-question">What Key Result do you want to drive?</div>
                    <div className="kr-required-badge">
                      Required <span className="kr-required-asterisk">*</span>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder={(() => {
                      if (currentKR.type === 'metric') {
                        const examples = [
                          "Increase weekly active users",
                          "Reduce average response time",
                          "Grow monthly revenue"
                        ];
                        return examples[keyResults.length % examples.length];
                      } else {
                        const examples = [
                          "Launch v1 onboarding flow",
                          "Publish pricing page",
                          "Complete user research interviews"
                        ];
                        return examples[keyResults.length % examples.length];
                      }
                    })()}
                    value={currentKR.title || ''}
                    onChange={(e) => setCurrentKR({ ...currentKR, title: e.target.value })}
                    className="kr-title-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && currentKR.title) {
                        e.target.nextElementSibling?.focus();
                      }
                    }}
                  />
                </div>

                {currentKR.type === 'metric' && (
                  <>
                    <div className={`metric-fields ${showAdvanced ? 'show-advanced' : ''}`}>
                      {showAdvanced && (
                        <div className="metric-field">
                          <div className="metric-field-label">Direction</div>
                          <select
                            value={currentKR.polarity || 'up'}
                            onChange={(e) => setCurrentKR({ ...currentKR, polarity: e.target.value })}
                          >
                            <option value="up">↑ Higher is better</option>
                            <option value="down">↓ Lower is better</option>
                          </select>
                        </div>
                      )}

                      <div className="metric-field">
                        <div className="metric-field-label">Target</div>
                        <input
                          type="number"
                          placeholder="e.g., 30"
                          value={currentKR.target || ''}
                          onChange={(e) => setCurrentKR({ ...currentKR, target: e.target.value })}
                        />
                      </div>

                      <div className="metric-field">
                        <div className="metric-field-label">Unit</div>
                        <input
                          type="text"
                          placeholder="%, $, users, hrs"
                          value={currentKR.unit || ''}
                          onChange={(e) => setCurrentKR({ ...currentKR, unit: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && currentKR.title && currentKR.target) {
                              addKR();
                            }
                          }}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="advanced-toggle"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                      {showAdvanced ? 'Hide direction' : 'Direction (optional)'}
                    </button>
                  </>
                )}

                {currentKR.type === 'milestone' && (
                  <input
                    type="date"
                    value={currentKR.dueDate || ''}
                    onChange={(e) => setCurrentKR({ ...currentKR, dueDate: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && currentKR.title && currentKR.dueDate) {
                        addKR();
                      }
                    }}
                  />
                )}

                <ActionButton
                  onClick={addKR}
                  isValid={!!(currentKR.title?.trim() && (currentKR.type === 'metric' ? currentKR.target : currentKR.dueDate))}
                  validationMessage={
                    !currentKR.title?.trim() 
                      ? 'Title is required' 
                      : currentKR.type === 'metric' 
                        ? 'Target value is required for metrics'
                        : 'Due date is required for milestones'
                  }
                  className="add-kr-btn"
                >
                  Add Key Result
                </ActionButton>
              </div>
            )}
          </div>

          {keyResults.length > 0 && (
            <div className="kr-list">
              <h3>Added Key Results ({keyResults.length})</h3>
              {keyResults.map((kr, index) => (
                <div key={kr.id} className="kr-item">
                  <span className="kr-number">{index + 1}</span>
                  <div className="kr-content">
                  <span className="kr-title">{kr.title}</span>
                    {kr.type === 'metric' && kr.target && (
                      <span className="kr-metric-detail">
                        {kr.polarity === 'down' ? '↓' : '↑'} {kr.target}{kr.unit ? ` ${kr.unit}` : ''}
                      </span>
                    )}
                    {kr.type === 'milestone' && kr.dueDate && (
                      <span className="kr-milestone-detail">
                        {new Date(kr.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                  <span className="kr-type">{kr.type}</span>
                  <button 
                    onClick={() => setKeyResults(keyResults.filter(k => k.id !== kr.id))}
                    className="remove-kr-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="wizard-btn secondary">
              Back
            </button>
            <button 
              onClick={completeWizard}
              disabled={keyResults.length === 0 || isSavingOKRs}
              className="wizard-btn"
            >
              {isSavingOKRs ? 'Saving...' : 'Review & Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Review Parsed OKRs Component
function ReviewParsedOKRs({ parsedData, onSave, onReparse, testSlackId, setToast, setFeedbackModal, selectedQuarter, setSelectedQuarter }) {
  const navigate = useNavigate();
  const [objectives, setObjectives] = useState(() => {
    // Initialize objectives with parsed data - use selectedQuarter from parent
    return parsedData.map((obj, idx) => ({
      id: `obj_${Date.now()}_${idx}`,
      title: obj.objective || '',
      quarter: selectedQuarter,
      keyResults: (obj.keyResults || []).map((kr, krIdx) => {
        // Handle both old format (strings) and new format (objects with type/description/target/unit)
        const isObject = typeof kr === 'object' && kr !== null;
        const isMetric = isObject && kr.type === 'metric';
        
        return {
          id: `kr_${Date.now()}_${idx}_${krIdx}`,
          type: isObject ? (kr.type || 'milestone') : 'milestone',
          title: isObject ? (kr.description || '') : kr,
          target: isMetric ? (kr.target || '') : '',
          unit: isMetric ? (kr.unit || '') : '',
          quarter: selectedQuarter
        };
      })
    }));
  });
  
  const [isSaving, setIsSaving] = useState(false);

  const updateObjective = (objId, field, value) => {
    setObjectives(objectives.map(obj => 
      obj.id === objId ? { ...obj, [field]: value } : obj
    ));
  };

  const updateKeyResult = (objId, krId, field, value) => {
    setObjectives(objectives.map(obj => 
      obj.id === objId ? {
        ...obj,
        keyResults: obj.keyResults.map(kr =>
          kr.id === krId ? { ...kr, [field]: value } : kr
        )
      } : obj
    ));
  };

  const deleteKeyResult = (objId, krId) => {
    setObjectives(objectives.map(obj =>
      obj.id === objId ? {
        ...obj,
        keyResults: obj.keyResults.filter(kr => kr.id !== krId)
      } : obj
    ));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const allSavedOKRs = [];
      
      for (const obj of objectives) {
        // Skip objectives with no key results
        if (obj.keyResults.length === 0) continue;
        
        const okrData = {
          objective: obj.title,
          keyResults: obj.keyResults.map(kr => ({
            title: kr.title,
            type: kr.type || 'Milestone',
            target: kr.target || null,
            unit: kr.unit || null,
            polarity: kr.polarity || 'Higher is better'
          })),
          owner: mockSession.userId,
          quarter: obj.quarter
        };
        
        // Save to database via API
        // In dev mode, use testSlackId; in prod, pass null to use session cookie
        const slackId = ENV_CONFIG.DEV_MODE ? testSlackId : null;
        const result = await saveOKRs(okrData, obj.quarter, slackId);
        
        allSavedOKRs.push({
          id: obj.id,
          objective: obj.title,
          keyResults: obj.keyResults,
          owner: mockSession.userId,
          quarter: obj.quarter,
          status: 'created',
          objectiveId: result.objective?.id,
          krIds: result.krs?.map(kr => kr.id) || []
        });
      }
      
      // Show prominent success modal for major milestone
      if (setFeedbackModal) {
        setFeedbackModal({
          type: 'success',
          title: 'Your OKRs are live',
          message: "They're now visible in My Plan.",
          action: {
            label: 'View My Plan',
            onClick: () => navigate('/plan')
          },
          dismissLabel: 'Stay here'
        });
      }
      
      onSave(allSavedOKRs);
    } catch (error) {
      console.error('Failed to save OKRs:', error);
      // Show prominent error modal for blocking failure
      if (setFeedbackModal) {
        setFeedbackModal({
          type: 'error',
          title: 'Could not save OKRs',
          message: error.message || 'Try again. Contact support if this continues.',
          dismissLabel: 'Close'
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const quarterOptions = generateQuarterOptions();

  return (
    <div className="page">
      <div className="review-parsed-header">
        <h1>Review Parsed OKRs</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
          <label style={{ fontSize: '15px', fontWeight: 500, color: 'var(--ink-main)' }}>
            Creating OKRs for
        </label>
        <select
            value={selectedQuarter}
            onChange={(e) => {
              const newQuarter = e.target.value;
              setSelectedQuarter(newQuarter);
              // Auto-apply to all objectives whenever user changes quarter
              setObjectives(objectives.map(obj => ({
                ...obj,
                quarter: newQuarter,
                keyResults: obj.keyResults.map(kr => ({
                  ...kr,
                  quarter: newQuarter
                }))
              })));
            }}
          className="quarter-select"
            style={{ fontSize: '15px', fontWeight: 600, color: 'var(--primary-blue)' }}
          >
            {quarterOptions.map(q => (
              <option key={q} value={q}>{q.replace('-Q', ' Q')}</option>
            ))}
        </select>
        </div>
        <p style={{ color: 'var(--ink-muted)', marginTop: '12px', fontSize: '14px' }}>
          Edit objectives and key results below, then save.
        </p>
      </div>

      {/* Objectives list */}
      <div className="parsed-objectives-list">
        {objectives.map((obj, objIdx) => (
          <div key={obj.id} className="parsed-objective-card">
            <div className="objective-header-row">
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink-muted)' }}>
                Objective {objIdx + 1}
              </span>
            </div>

            <input
              type="text"
              value={obj.title}
              onChange={(e) => updateObjective(obj.id, 'title', e.target.value)}
              className="objective-input"
              placeholder="Objective title"
            />

            {/* Key Results */}
            <div className="parsed-kr-list">
              {obj.keyResults.map((kr, krIdx) => (
                <div key={kr.id} className="parsed-kr-row">
                  <div className="kr-type-badge" data-type={kr.type}>
                    {kr.type === 'metric' ? '📊' : '🎯'}
                  </div>
                  <span className="kr-number">KR {krIdx + 1}</span>
                  <input
                    type="text"
                    value={kr.title}
                    onChange={(e) => updateKeyResult(obj.id, kr.id, 'title', e.target.value)}
                    className="kr-input"
                    placeholder="Key result description"
                  />
                  {kr.type === 'metric' ? (
                    <>
                      <input
                        type="text"
                        value={kr.target}
                        onChange={(e) => updateKeyResult(obj.id, kr.id, 'target', e.target.value)}
                        className="kr-target-input-small"
                        placeholder="Target"
                        title="Target value"
                      />
                      <input
                        type="text"
                        value={kr.unit}
                        onChange={(e) => updateKeyResult(obj.id, kr.id, 'unit', e.target.value)}
                        className="kr-unit-input"
                        placeholder="Unit"
                        title="Unit of measurement"
                      />
                    </>
                  ) : (
                    <span className="kr-milestone-label">Milestone</span>
                  )}
                  <button
                    onClick={() => deleteKeyResult(obj.id, kr.id)}
                    className="delete-kr-btn"
                    title="Delete key result"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="review-actions">
        <button
          onClick={onReparse}
          className="ghost-btn"
          disabled={isSaving}
        >
          ← Re-parse Text
        </button>
        <button
          onClick={handleSave}
          className="save-btn"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save OKRs'}
        </button>
      </div>
    </div>
  );
}

// Edit Mode Component
// eslint-disable-next-line no-unused-vars
function EditMode({ testSlackId, setToast, setFeedbackModal, currentQuarter, onBack, onSave }) {
  const [isLoading, setIsLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter);
  const [editingObjective, setEditingObjective] = useState(null); // { id, name, quarter }
  const [editingKR, setEditingKR] = useState(null); // { id, text, type, target, unit, polarity }
  const [isSaving, setIsSaving] = useState(false);

  // Load OKRs for the selected quarter
  useEffect(() => {
    const loadOKRs = async () => {
      setIsLoading(true);
      try {
        const data = await getOKRs('me', selectedQuarter, testSlackId);
        setObjectives(data.objectives || []);
      } catch (error) {
        console.error('Failed to load OKRs:', error);
        setToast('Failed to load OKRs');
        setTimeout(() => setToast(null), 4000);
      } finally {
        setIsLoading(false);
      }
    };

    loadOKRs();
  }, [selectedQuarter, testSlackId, setToast]);

  const handleUpdateObjective = async (objectiveId, updates) => {
    setIsSaving(true);
    try {
      await updateObjective(objectiveId, updates, testSlackId);
      setToast('Objective updated');
      setTimeout(() => setToast(null), 3000);
      setEditingObjective(null);
      
      // Reload OKRs
      const data = await getOKRs('me', selectedQuarter, testSlackId);
      setObjectives(data.objectives || []);
      
      // Call onSave to refresh My Plan if provided
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to update objective:', error);
      setToast('Failed to update objective');
      setTimeout(() => setToast(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateKR = async (krId, updates) => {
    setIsSaving(true);
    try {
      await updateKR(krId, updates, testSlackId);
      setToast('KR updated');
      setTimeout(() => setToast(null), 3000);
      setEditingKR(null);
      
      // Reload OKRs
      const data = await getOKRs('me', selectedQuarter, testSlackId);
      setObjectives(data.objectives || []);
      
      // Call onSave to refresh My Plan if provided
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to update KR:', error);
      setToast('Failed to update KR');
      setTimeout(() => setToast(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="spinner"></div>
          <p>Loading your OKRs...</p>
        </div>
      </div>
    );
  }

  if (objectives.length === 0) {
    return (
      <div className="page">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h2>No OKRs found for {selectedQuarter}</h2>
          <p style={{ marginTop: '1rem', color: '#666' }}>Create some OKRs first using Teach Mode or Quick Mode.</p>
          <button onClick={onBack} className="primary-btn" style={{ marginTop: '2rem' }}>Back to My Plan</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button 
              onClick={onBack} 
              style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: 'transparent', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '0.9rem',
                color: '#666'
              }}
            >
              ← Back
            </button>
            <h1 style={{ margin: 0 }}>Edit OKRs</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.9rem', color: '#666' }}>Quarter:</label>
            <select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              style={{ padding: '0.5rem 1rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
            >
              <option value="2025-Q4">2025 Q4</option>
              <option value="2026-Q1">2026 Q1</option>
              <option value="2026-Q2">2026 Q2</option>
              <option value="2026-Q3">2026 Q3</option>
              <option value="2026-Q4">2026 Q4</option>
            </select>
          </div>
        </div>
      </div>

      {/* Objectives List */}
      <div style={{ marginTop: '1rem' }}>
          {objectives.map((obj) => (
            <div key={obj.id} style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#fafafa' }}>
              {/* Objective Header */}
              <div style={{ marginBottom: '1.5rem' }}>
                {editingObjective?.id === obj.id ? (
                  <div style={{ backgroundColor: '#fff', padding: '1rem', borderRadius: '6px', border: '2px solid var(--primary-blue)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.25rem', color: '#666' }}>Objective Name</label>
                        <input
                          type="text"
                          value={editingObjective.name}
                          onChange={(e) => setEditingObjective({ ...editingObjective, name: e.target.value })}
                          style={{ width: '100%', padding: '0.6rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1rem' }}
                          placeholder="Objective name"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.25rem', color: '#666' }}>Quarter (affects all KRs)</label>
                        <select
                          value={editingObjective.quarter}
                          onChange={(e) => setEditingObjective({ ...editingObjective, quarter: e.target.value })}
                          style={{ width: '100%', padding: '0.6rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1rem' }}
                        >
                          <option value="2025-Q4">2025 Q4</option>
                          <option value="2026-Q1">2026 Q1</option>
                          <option value="2026-Q2">2026 Q2</option>
                          <option value="2026-Q3">2026 Q3</option>
                          <option value="2026-Q4">2026 Q4</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <button
                          onClick={() => handleUpdateObjective(obj.id, { name: editingObjective.name, quarter: editingObjective.quarter })}
                          disabled={isSaving}
                          className="primary-btn"
                          style={{ flex: 1 }}
                        >
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          onClick={() => setEditingObjective(null)}
                          disabled={isSaving}
                          className="secondary-btn"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>{obj.name}</h3>
                      <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.25rem' }}>Quarter: {obj.quarter}</div>
                    </div>
                    <button
                      onClick={() => setEditingObjective({ id: obj.id, name: obj.name, quarter: obj.quarter })}
                      className="secondary-btn"
                      style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                    >
                      Edit Objective
                    </button>
                  </div>
                )}
              </div>

              {/* Key Results */}
              <div style={{ marginTop: '1rem' }}>
                {obj.keyResults.map((kr) => (
                  <div key={kr.id} style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                    {editingKR?.id === kr.id ? (
                      <div style={{ padding: '0.5rem', border: '2px solid var(--primary-blue)', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', marginBottom: '0.25rem', color: '#666' }}>Description</label>
                            <input
                              type="text"
                              value={editingKR.text}
                              onChange={(e) => setEditingKR({ ...editingKR, text: e.target.value })}
                              placeholder="KR description"
                              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                          </div>
                          
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', marginBottom: '0.25rem', color: '#666' }}>Type</label>
                            <select
                              value={editingKR.type}
                              onChange={(e) => setEditingKR({ ...editingKR, type: e.target.value })}
                              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                            >
                              <option value="Metric">Metric (measurable)</option>
                              <option value="Milestone">Milestone (deliverable)</option>
                            </select>
                          </div>

                          {editingKR.type === 'Metric' && (
                            <>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', marginBottom: '0.25rem', color: '#666' }}>Polarity</label>
                                <select
                                  value={editingKR.polarity || 'Higher is better'}
                                  onChange={(e) => setEditingKR({ ...editingKR, polarity: e.target.value })}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                                >
                                  <option value="Higher is better">↑ Higher is better</option>
                                  <option value="Lower is better">↓ Lower is better</option>
                                </select>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', marginBottom: '0.25rem', color: '#666' }}>Target</label>
                                  <input
                                    type="number"
                                    value={editingKR.target || ''}
                                    onChange={(e) => setEditingKR({ ...editingKR, target: e.target.value })}
                                    placeholder="100"
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', marginBottom: '0.25rem', color: '#666' }}>Unit</label>
                                  <input
                                    type="text"
                                    value={editingKR.unit || ''}
                                    onChange={(e) => setEditingKR({ ...editingKR, unit: e.target.value })}
                                    placeholder="%, $, users"
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <button
                              onClick={() => handleUpdateKR(kr.id, editingKR)}
                              disabled={isSaving}
                              className="primary-btn"
                              style={{ flex: 1 }}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => setEditingKR(null)}
                              disabled={isSaving}
                              className="secondary-btn"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{kr.text}</div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                            {kr.type === 'Metric' && kr.target && kr.unit ? (
                              <>Target: {kr.target} {kr.unit} · {kr.polarity || 'Higher is better'}</>
                            ) : (
                              <>Milestone</>
                            )}
                            {' · '}
                            Progress: {kr.progress}%
                          </div>
                        </div>
                        <button
                          onClick={() => setEditingKR({ 
                            id: kr.id, 
                            text: kr.text, 
                            type: kr.type, 
                            target: kr.target || '', 
                            unit: kr.unit || '', 
                            polarity: kr.polarity || 'Higher is better' 
                          })}
                          className="secondary-btn"
                          style={{ fontSize: '0.85rem', padding: '0.4rem 0.7rem' }}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
  );
}

// Quick Mode Component
function QuickMode({ onParsed, testSlackId, setToast, setFeedbackModal, currentQuarter, setCurrentQuarter }) {
  const [text, setText] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  const parseText = async () => {
    setIsParsing(true);
    
    try {
      // Parse the actual user text with GPT
      const parsedObjectives = await parseOKRs(text, testSlackId);
      
      if (!parsedObjectives || parsedObjectives.length === 0) {
        throw new Error('No OKRs found in the text. Please check your input format.');
      }
      
      // Pass parsed data to parent for review (don't save yet)
      onParsed(parsedObjectives);
    } catch (error) {
      console.error('Failed to parse OKRs:', error);
      // Show prominent error modal for blocking failure
      if (setFeedbackModal) {
        setFeedbackModal({
          type: 'error',
          title: 'Could not parse OKRs',
          message: error.message || 'Check your formatting and try again.',
          dismissLabel: 'Close'
        });
      }
    } finally {
      setIsParsing(false);
    }
  };

  const quarterOptions = generateQuarterOptions();

  return (
    <div className="page">
      <h1>Quick Mode</h1>
      <p style={{ color: 'var(--ink-muted)', marginBottom: '12px' }}>Type or paste your OKRs in any format below — we'll handle the structure</p>
      
      {/* Quarter selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <label style={{ fontSize: '15px', fontWeight: 500, color: 'var(--ink-main)' }}>
          Creating for
        </label>
        <select
          value={currentQuarter}
          onChange={(e) => setCurrentQuarter(e.target.value)}
          className="quarter-select"
          style={{ fontSize: '15px', fontWeight: 600, color: 'var(--primary-blue)' }}
        >
          {quarterOptions.map(q => (
            <option key={q} value={q}>{q.replace('-Q', ' Q')}</option>
          ))}
        </select>
      </div>
      
      <div className="paste-form">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Example:&#10;Objective: Improve team productivity&#10;Q2 focus&#10;Reduce bugs&#10;Ship CI/CD by March"
          rows={12}
          className="paste-textarea"
        />
        
        <button 
          onClick={parseText}
          disabled={!text.trim() || isParsing}
          className="parse-btn"
        >
          {isParsing ? 'Creating...' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// Review Screen Component
function ReviewScreen({ okrs, onBack }) {
  const [editingKR, setEditingKR] = useState(null);
  const [proposals, setProposals] = useState([]);

  const calculateScore = (kr) => {
    if (kr.type === 'metric') {
      if (!kr.actual || !kr.target) return null;
      const ratio = kr.polarity === 'up' ? kr.actual / kr.target : kr.target / kr.actual;
      return Math.min(ratio, 1); // Clamp to 1.0 max
    } else if (kr.type === 'milestone') {
      return kr.status === 'done' ? 1 : 0;
    }
    return null;
  };

  const calculateObjectiveScore = (objective) => {
    const scores = objective.keyResults.map(calculateScore).filter(s => s !== null);
    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  };

  const saveChanges = (kr, changes) => {
    if (mockQuarterLock.isLocked) {
      // Post-lock: create proposal
      const proposal = {
        id: Date.now(),
        krId: kr.id,
        changes,
        reason: 'Updated via OKR Editor', // Default reason instead of prompt()
        timestamp: new Date().toISOString()
      };
      setProposals([...proposals, proposal]);
    } else {
      // Pre-lock: autosave
      // TODO: Implement autosave to backend
      console.log('Autosaving changes:', changes);
    }
    setEditingKR(null);
  };

  return (
    <div className="page">
      <div className="review-header">
        <button onClick={onBack} className="back-btn">← Back to Editor</button>
        <h1>Review & Edit OKRs</h1>
        <div className="quarter-status">
          {mockQuarterLock.isLocked ? (
            <span className="locked-badge">🔒 Quarter Locked</span>
          ) : (
            <span className="unlocked-badge">✅ Quarter Open</span>
          )}
        </div>
      </div>

      {/* Draft Status Banner */}
      {okrs.some(okr => okr.status === 'draft') && (
        <div className="draft-banner">
          <div className="draft-content">
            <span className="draft-icon">⏳</span>
            <div className="draft-text">
              <strong>Draft OKRs Awaiting Approval</strong>
              <p>Your OKRs have been submitted for review. They'll appear in your dashboard once approved by your manager.</p>
            </div>
          </div>
        </div>
      )}

      {okrs.map((objective) => (
        <div key={objective.id} className="objective-accordion">
          <div className="objective-header">
            <h2>{objective.objective}</h2>
            <div className="objective-score">
              Score: {calculateObjectiveScore(objective)?.toFixed(2) || 'N/A'}
            </div>
          </div>
          
          <div className="kr-grid">
            <div className="kr-grid-header">
              <span>Title</span>
              <span>Type</span>
              <span>Target/Status</span>
              <span>Owner</span>
              <span>Score</span>
              <span>Actions</span>
            </div>
            
            {objective.keyResults.map((kr) => (
              <div key={kr.id} className="kr-grid-row">
                <span className="kr-title">{kr.title}</span>
                <span className="kr-type">{kr.type}</span>
                <span className="kr-target">
                  {kr.type === 'metric' ? (
                    `${kr.polarity === 'up' ? '↑' : '↓'} ${kr.target}${kr.unit}`
                  ) : (
                    kr.dueDate
                  )}
                </span>
                <span className="kr-owner">{kr.owner}</span>
                <span className="kr-score">
                  {calculateScore(kr)?.toFixed(2) || 'N/A'}
                </span>
                <div className="kr-actions">
                  <button 
                    onClick={() => setEditingKR(kr)}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      {editingKR && (
        <div className="edit-modal">
          <div className="modal-content">
            <h3>Edit Key Result</h3>
            
            {editingKR.type === 'metric' && (
              <div className="edit-fields">
                <input
                  type="text"
                  placeholder="Title"
                  defaultValue={editingKR.title}
                  onChange={(e) => setEditingKR({ ...editingKR, title: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="Actual value"
                  defaultValue={editingKR.actual}
                  onChange={(e) => setEditingKR({ ...editingKR, actual: parseFloat(e.target.value) })}
                />
                <input
                  type="text"
                  placeholder="Owner"
                  defaultValue={editingKR.owner}
                  onChange={(e) => setEditingKR({ ...editingKR, owner: e.target.value })}
                />
              </div>
            )}

            {editingKR.type === 'milestone' && (
              <div className="edit-fields">
                <input
                  type="text"
                  placeholder="Title"
                  defaultValue={editingKR.title}
                  onChange={(e) => setEditingKR({ ...editingKR, title: e.target.value })}
                />
                <select
                  defaultValue={editingKR.status || 'pending'}
                  onChange={(e) => setEditingKR({ ...editingKR, status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="done">Done</option>
                </select>
                <input
                  type="text"
                  placeholder="Owner"
                  defaultValue={editingKR.owner}
                  onChange={(e) => setEditingKR({ ...editingKR, owner: e.target.value })}
                />
              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => setEditingKR(null)} className="cancel-btn">
                Cancel
              </button>
              <button 
                onClick={() => saveChanges(editingKR, editingKR)}
                className="save-btn"
              >
                {mockQuarterLock.isLocked ? 'Propose Changes' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proposals List */}
      {proposals.length > 0 && (
        <div className="proposals-section">
          <h3>Pending Proposals ({proposals.length})</h3>
          {proposals.map((proposal) => (
            <div key={proposal.id} className="proposal-item">
              <span className="proposal-kr">KR: {proposal.changes.title}</span>
              <span className="proposal-reason">{proposal.reason}</span>
              <span className="proposal-date">{new Date(proposal.timestamp).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Not Enabled Page Component
function NotEnabledPage() {
  const navigate = useNavigate();
  
  // Check if user is now enabled (poll every 5 seconds or on mount)
  useEffect(() => {
    const checkEnabled = async () => {
      // Clear cache to force fresh check from server
      sessionCheckCache.value = null;
      sessionCheckCache.enabled = null;
      sessionCheckCache.timestamp = 0;
      
      const status = await hasValidSession();
      if (status.authenticated && status.enabled) {
        // User is now enabled, redirect to app
        navigate('/overview', { replace: true });
      }
    };
    
    // Check immediately
    checkEnabled();
    
    // Then check every 5 seconds
    const interval = setInterval(checkEnabled, 5000);
    
    return () => clearInterval(interval);
  }, [navigate]);
  
  const handleLogout = async () => {
    // Call backend logout endpoint to clear HttpOnly cookie
    try {
      await fetch(`${ENV_CONFIG.API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear session cache
    sessionCheckCache.value = null;
    sessionCheckCache.enabled = null;
    sessionCheckCache.timestamp = 0;
    // Redirect to login
    window.location.href = '/login';
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>DecisionOS</h1>
          <p className="login-tagline">Focus on what matters. Let go of the rest.</p>
        </div>

        <div className="login-content">
          <div className="status-message">
            <h2>Access Not Enabled</h2>
            <p>Your account exists, but access hasn't been granted.</p>
            <p>Contact your admin to enable your account.</p>
          </div>
          
          <button 
            className="slack-signin-button" 
            onClick={handleLogout}
            style={{ marginTop: '24px', background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1' }}
          >
            Sign Out
          </button>
          
          <p className="login-footer-text">Need help? Contact your admin to get enabled.</p>
        </div>
      </div>
    </div>
  );
}

// Login Page Component - Slack OAuth Sign In
function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // If already logged in, check if enabled and redirect accordingly
  // Add small delay to allow cookie to be set after OAuth redirect
  useEffect(() => {
    const checkAuth = async () => {
      // Small delay to ensure cookie is available after OAuth redirect
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const status = await hasValidSession();
      if (status.authenticated) {
        if (!status.enabled) {
          navigate('/not-enabled', { replace: true });
        } else {
          const from = location.state?.from?.pathname || '/overview';
          navigate(from, { replace: true });
        }
      }
    };
    
    checkAuth();
  }, [location, navigate]);
  
  const handleSignIn = () => {
    // Get redirect URL after login
    const redirectUrl = location.state?.from?.pathname 
      ? `${ENV_CONFIG.FRONTEND_URL}${location.state.from.pathname}`
      : ENV_CONFIG.FRONTEND_URL;
    
    // Direct redirect to OAuth endpoint (no fetch, no CORS preflight)
    // The worker will generate the Slack OAuth URL and redirect to Slack
    window.location.href = `${ENV_CONFIG.API_URL}/api/auth/slack/start?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>DecisionOS</h1>
          <p className="login-tagline">Focus on what matters. Let go of the rest.</p>
        </div>

        <div className="login-content">
          <p className="login-instruction">Sign in with your Slack account to get started</p>
          
          {/* Slack Sign In Button - Following Slack Design Guidelines */}
          <button 
            className="slack-signin-button" 
            onClick={handleSignIn}
            aria-label="Sign in with Slack"
          >
            <svg 
              className="slack-logo" 
              viewBox="0 0 54 54" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <g fill="none" fillRule="evenodd">
                <path d="M19.712.133a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386h5.376V5.52A5.381 5.381 0 0 0 19.712.133m0 14.365H5.376A5.381 5.381 0 0 0 0 19.884a5.381 5.381 0 0 0 5.376 5.387h14.336a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386" fill="#36C5F0"/>
                <path d="M53.76 19.884a5.381 5.381 0 0 0-5.376-5.386 5.381 5.381 0 0 0-5.376 5.386v5.387h5.376a5.381 5.381 0 0 0 5.376-5.387m-14.336 0V5.52A5.381 5.381 0 0 0 34.048.133a5.381 5.381 0 0 0-5.376 5.387v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387" fill="#2EB67D"/>
                <path d="M34.048 54a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386h-5.376v5.386A5.381 5.381 0 0 0 34.048 54m0-14.365h14.336a5.381 5.381 0 0 0 5.376-5.386 5.381 5.381 0 0 0-5.376-5.387H34.048a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386" fill="#ECB22E"/>
                <path d="M0 34.249a5.381 5.381 0 0 0 5.376 5.386 5.381 5.381 0 0 0 5.376-5.386v-5.387H5.376A5.381 5.381 0 0 0 0 34.25m14.336 0v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387V34.25a5.381 5.381 0 0 0-5.376-5.387 5.381 5.381 0 0 0-5.376 5.387" fill="#E01E5A"/>
              </g>
            </svg>
            <span className="slack-signin-text">Sign in with Slack</span>
          </button>

          <p className="login-footer-text">
            Need access? Contact your admin to get enabled.
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
